export const REACT_NATIVE_DEVELOPER_ROADMAP = [
  {
    phaseKey: "foundation",
    phase: 1,
    title: "Foundations & React Native Basics",
    level: "Beginner",
    startWeek: 1,
    endWeek: 4,
    weeks: [
      {
        week: 1,
        title: "JavaScript Essentials for React Native",
        weekGoal:
          "Build strong JavaScript foundations required for React Native development",
        weekOutcome:
          "Confidently write and reason about JavaScript logic used inside React Native components",
        miniProject:
          "JS Logic Playground – Async data handling, array transformations, and utility functions for mobile apps",
        canLearn: ["students", "beginners"],
        days: [
          {
            day: 1,
            topic: "JavaScript Execution Model & Variables",
            subTopics: [
              "How JavaScript runs",
              "Call stack",
              "var vs let vs const",
              "Block scope",
              "Scope issues in React Native",
            ],
            explanation:
              "Understand how JavaScript executes code and how variables behave in different scopes to avoid common React Native bugs.",
            content: {
              introduction:
                "React Native is powered entirely by JavaScript. Most bugs in mobile apps come from weak understanding of how JavaScript executes code and handles variables.",
              sections: [
                {
                  title: "JavaScript Execution Model",
                  description:
                    "JavaScript runs code line by line using a call stack.",
                  keyPoints: [
                    "JavaScript is single-threaded",
                    "Functions are pushed to the call stack",
                    "Execution happens top to bottom",
                  ],
                  analogy:
                    "Like a stack of plates – last function in runs first",
                },
                {
                  title: "Variables: var, let, const",
                  description:
                    "Variables control how data is stored and accessed.",
                  keyPoints: [
                    "var is function scoped (avoid)",
                    "let and const are block scoped",
                    "Use const by default",
                  ],
                  code: "const name = 'Skilll.dev';\nlet count = 0;",
                },
                {
                  title: "Why Scope Matters in React Native",
                  description:
                    "Wrong scope causes stale state, broken lists, and unexpected re-renders.",
                },
              ],
              commonMistakes: [
                {
                  mistake: "Using var inside loops",
                  consequence: "Unexpected behavior in lists and callbacks",
                  fix: "Use let or const",
                },
              ],
              proTips: [
                "Always start with const",
                "Understand scope before using state",
                "JS bugs are harder than UI bugs",
              ],
            },
            tasks: [
              "Create examples using let and const",
              "Test scope behavior inside loops",
            ],
            examples: ["for (let i = 0; i < 3; i++) { console.log(i); }"],
            useCases: [
              "Component state logic",
              "Event handlers",
              "Async callbacks",
            ],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Understand JS execution",
              "Use correct variable types",
              "Avoid scope-related bugs",
            ],
          },
          {
            day: 2,
            topic: "Functions, Arrow Functions & Closures",
            subTopics: [
              "Function declarations",
              "Arrow functions",
              "Closures",
              "Closures in hooks",
            ],
            explanation:
              "Learn how functions and closures work — the backbone of hooks like useState and useEffect.",
            content: {
              introduction:
                "Functions are everywhere in React Native — components, hooks, callbacks. Closures explain how hooks remember values.",
              sections: [
                {
                  title: "Arrow Functions",
                  description:
                    "Arrow functions provide cleaner syntax and lexical scope.",
                  code: "const add = (a, b) => a + b;",
                },
                {
                  title: "Closures",
                  description:
                    "Closures allow a function to remember variables from its outer scope.",
                  analogy: "Like a backpack a function carries with it",
                },
              ],
            },
            tasks: [
              "Convert normal functions to arrow functions",
              "Create a closure example",
            ],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Understand closures",
              "Use arrow functions correctly",
              "Relate closures to hooks",
            ],
          },
          {
            day: 3,
            topic: "Arrays, Objects & Immutability",
            subTopics: [
              "map, filter, reduce",
              "Object spread",
              "Immutability",
              "Why mutation breaks React",
            ],
            explanation:
              "Learn how to safely manipulate data without breaking React Native rendering.",
            content: {
              introduction:
                "Rendering lists and updating state depends on immutable data operations.",
              sections: [
                {
                  title: "Array Methods",
                  description:
                    "map, filter, and reduce are used everywhere in React Native.",
                  code: "items.map(item => item.title)",
                },
                {
                  title: "Immutability",
                  description: "Never mutate state directly.",
                  code: "setItems([...items, newItem])",
                },
              ],
            },
            tasks: ["Render a list using map", "Update object data immutably"],
            editorType: "javascript",
            estimatedTime: "30 minutes",
            objectives: [
              "Use array methods confidently",
              "Avoid state mutation",
              "Understand render triggers",
            ],
          },
          {
            day: 4,
            topic: "Async JavaScript & Error Handling",
            subTopics: [
              "Promises",
              "async/await",
              "try/catch",
              "Async bugs in apps",
            ],
            explanation:
              "Async code powers APIs, storage, and authentication in mobile apps.",
            content: {
              introduction:
                "Most crashes in mobile apps are caused by poorly handled async code.",
              sections: [
                {
                  title: "Async/Await",
                  description: "Cleaner way to handle asynchronous logic.",
                  code: "const data = await fetchData();",
                },
                {
                  title: "Error Handling",
                  description: "Always handle failures gracefully.",
                },
              ],
            },
            tasks: [
              "Write async function with try/catch",
              "Simulate API failure handling",
            ],
            editorType: "javascript",
            estimatedTime: "30 minutes",
            objectives: [
              "Handle async logic safely",
              "Prevent crashes",
              "Understand promise flow",
            ],
          },
          {
            day: 5,
            topic: "Modern ES6 Patterns",
            subTopics: [
              "Destructuring",
              "Spread operator",
              "Default parameters",
            ],
            explanation:
              "Write clean, professional JavaScript used in real React Native projects.",
            content: {
              sections: [
                {
                  title: "Destructuring",
                  code: "const { name, age } = user;",
                },
                {
                  title: "Spread Operator",
                  code: "const newState = { ...state, loading: true };",
                },
              ],
            },
            tasks: [
              "Refactor code using destructuring",
              "Use spread operator for state updates",
            ],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Write cleaner JS",
              "Reduce boilerplate",
              "Improve readability",
            ],
          },
          {
            day: 6,
            topic: "JavaScript for Mobile Mindset",
            subTopics: [
              "Memory considerations",
              "Avoid heavy loops",
              "Mobile performance impact",
            ],
            explanation:
              "Learn how JavaScript decisions impact mobile performance.",
            content: {
              sections: [
                {
                  title: "Mobile Constraints",
                  description: "Mobile devices have limited CPU and memory.",
                },
              ],
            },
            tasks: ["Optimize a loop", "Avoid unnecessary calculations"],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Think performance-first",
              "Avoid heavy logic",
              "Write mobile-friendly JS",
            ],
          },
          {
            day: 7,
            topic: "Week 1 Review & Mini Project",
            subTopics: [
              "JS fundamentals recap",
              "Async logic",
              "Data manipulation",
            ],
            explanation:
              "Apply all concepts by building a logic-focused mini project.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description:
                    "Build a JS utility module used in a React Native app.",
                },
              ],
            },
            tasks: [
              "Build utility functions",
              "Handle async data",
              "Refactor using ES6",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Apply JS concepts",
              "Build confidence",
              "Prepare for React Native",
            ],
          },
        ],
      },
      {
        week: 2,
        title: "React Fundamentals for React Native",
        weekGoal:
          "Understand core React concepts and how they translate to React Native components",
        weekOutcome:
          "Build functional, reusable React Native components using hooks and props",
        miniProject:
          "Basic React Native Screens – Counter, Profile Card, and Conditional UI",
        canLearn: ["students", "beginners"],
        days: [
          {
            day: 1,
            topic: "What is React & Component-Based Thinking",
            subTopics: [
              "What is React",
              "Component-based architecture",
              "React vs React Native",
              "JSX basics",
            ],
            explanation:
              "Understand React’s component-based mindset and how JSX is used to describe UI in React Native.",
            content: {
              introduction:
                "React Native is built on React. Before learning mobile-specific APIs, you must understand how React thinks in terms of components and UI composition.",
              sections: [
                {
                  title: "What is React?",
                  description:
                    "React is a JavaScript library for building user interfaces using reusable components.",
                  keyPoints: [
                    "UI is broken into components",
                    "Components manage their own data",
                    "UI updates automatically when state changes",
                  ],
                  analogy:
                    "Like building with LEGO blocks — small pieces combine to form a big structure",
                },
                {
                  title: "Component-Based Architecture",
                  description: "Each screen or UI part is a component.",
                  keyPoints: [
                    "Components are reusable",
                    "Each component has a single responsibility",
                    "Large apps are built by composing small components",
                  ],
                },
                {
                  title: "React vs React Native",
                  description:
                    "React Native uses React concepts but renders native UI instead of HTML.",
                  comparison: [
                    "React → Web (div, span)",
                    "React Native → Mobile (View, Text)",
                    "Logic is same, rendering target is different",
                  ],
                },
                {
                  title: "JSX Basics",
                  description: "JSX allows writing UI inside JavaScript.",
                  code: "const App = () => (\n  <View>\n    <Text>Hello React Native</Text>\n  </View>\n);",
                },
              ],
              commonMistakes: [
                {
                  mistake: "Thinking JSX is HTML",
                  consequence: "Using invalid tags in React Native",
                  fix: "Use View, Text, Image instead of div, p, img",
                },
              ],
              proTips: [
                "Think UI as components, not pages",
                "JSX is just JavaScript under the hood",
                "React logic stays same across platforms",
              ],
            },
            tasks: [
              "Create a simple functional component",
              "Break a screen into 3 components",
            ],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Understand React basics",
              "Adopt component-based thinking",
              "Differentiate React vs React Native",
            ],
          },
          {
            day: 2,
            topic: "Functional Components & Props",
            subTopics: [
              "Functional components",
              "Props",
              "Passing data",
              "Reusable components",
            ],
            explanation:
              "Learn how to pass data between components using props and build reusable UI blocks.",
            content: {
              introduction:
                "Props allow components to receive data from their parent, making them dynamic and reusable.",
              sections: [
                {
                  title: "Functional Components",
                  description:
                    "Modern React uses functions instead of class components.",
                  code: "const Greeting = () => <Text>Hello</Text>;",
                },
                {
                  title: "Props Explained",
                  description: "Props are inputs to components.",
                  code: "const Greeting = ({ name }) => <Text>Hello {name}</Text>;",
                },
                {
                  title: "Why Props Matter",
                  description: "Props make components reusable across screens.",
                },
              ],
            },
            tasks: [
              "Create a Card component",
              "Pass title and description via props",
            ],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Use props correctly",
              "Build reusable components",
              "Avoid hardcoded UI",
            ],
          },
          {
            day: 3,
            topic: "useState Hook & State Management Basics",
            subTopics: [
              "What is state",
              "useState hook",
              "State updates",
              "Re-rendering",
            ],
            explanation:
              "Learn how React Native components store and update dynamic data using state.",
            content: {
              introduction:
                "State represents data that can change over time and directly controls what users see.",
              sections: [
                {
                  title: "What is State?",
                  description: "State is local data managed by a component.",
                },
                {
                  title: "useState Hook",
                  description:
                    "useState lets components store and update data.",
                  code: "const [count, setCount] = useState(0);",
                },
                {
                  title: "Re-rendering",
                  description: "Updating state automatically updates UI.",
                },
              ],
              commonMistakes: [
                {
                  mistake: "Mutating state directly",
                  consequence: "UI does not update correctly",
                  fix: "Always use state setter function",
                },
              ],
            },
            tasks: ["Build a counter", "Update state on button press"],
            editorType: "javascript",
            estimatedTime: "30 minutes",
            objectives: [
              "Understand state",
              "Use useState hook",
              "Trigger re-renders correctly",
            ],
          },
          {
            day: 4,
            topic: "Handling Events & User Interaction",
            subTopics: [
              "onPress events",
              "Event handlers",
              "Passing functions as props",
            ],
            explanation:
              "Handle user actions like button presses and connect UI with logic.",
            content: {
              introduction:
                "Mobile apps are driven by user interaction. React Native connects UI with logic using event handlers.",
              sections: [
                {
                  title: "Handling Events",
                  description: "Events respond to user actions.",
                  code: "<Button title='Click' onPress={handleClick} />",
                },
                {
                  title: "Passing Functions as Props",
                  description: "Child components can trigger parent logic.",
                },
              ],
            },
            tasks: [
              "Handle button press",
              "Trigger parent function from child",
            ],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Handle user events",
              "Connect UI and logic",
              "Understand event flow",
            ],
          },
          {
            day: 5,
            topic: "Conditional Rendering",
            subTopics: [
              "Conditional UI",
              "Ternary operator",
              "Logical AND rendering",
            ],
            explanation: "Show or hide UI based on state or props.",
            content: {
              sections: [
                {
                  title: "Conditional Rendering",
                  description: "Render UI based on conditions.",
                  code: "{isLoggedIn ? <Home /> : <Login />}",
                },
              ],
            },
            tasks: ["Show loader conditionally", "Toggle UI using state"],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Control UI flow",
              "Build dynamic screens",
              "Avoid unnecessary screens",
            ],
          },
          {
            day: 6,
            topic: "Component Composition & Best Practices",
            subTopics: [
              "Component composition",
              "Splitting components",
              "Clean component structure",
            ],
            explanation:
              "Learn how to structure components cleanly for scalable apps.",
            content: {
              sections: [
                {
                  title: "Component Composition",
                  description:
                    "Build complex UI by combining small components.",
                },
              ],
            },
            tasks: [
              "Refactor a large component",
              "Split UI into smaller components",
            ],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Write clean components",
              "Improve readability",
              "Prepare for large apps",
            ],
          },
          {
            day: 7,
            topic: "Week 2 Review & Mini Project",
            subTopics: [
              "Components",
              "Props",
              "State",
              "Events",
              "Conditional rendering",
            ],
            explanation:
              "Apply all React fundamentals by building interactive React Native screens.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description:
                    "Build 2–3 small interactive screens using React fundamentals.",
                },
              ],
            },
            tasks: [
              "Build profile card component",
              "Create counter screen",
              "Apply conditional rendering",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Apply React fundamentals",
              "Build confidence",
              "Get ready for core React Native UI",
            ],
          },
        ],
      },
      {
        week: 3,
        title: "Core React Native Components & Styling Basics",
        weekGoal:
          "Learn core React Native building blocks and understand how UI is created on mobile",
        weekOutcome:
          "Build clean, responsive screens using core components and StyleSheet",
        miniProject:
          "Basic Mobile Screens – Home Screen, Profile Screen & Settings Screen UI",
        canLearn: ["students", "beginners"],
        days: [
          {
            day: 1,
            topic: "Understanding React Native Core Components",
            subTopics: [
              "What are core components",
              "View component",
              "Text component",
              "Difference from HTML elements",
            ],
            explanation:
              "Learn the most fundamental React Native components that replace HTML tags and form the base of every mobile UI.",
            content: {
              introduction:
                "React Native does not use HTML. Instead, it provides native components that map directly to Android and iOS UI elements.",
              sections: [
                {
                  title: "View Component",
                  description:
                    "View is the basic container used for layout and structure.",
                  keyPoints: [
                    "Works like div but maps to native UI",
                    "Supports flexbox layout",
                    "Can contain other components",
                  ],
                  code: "<View>\n  <Text>Hello</Text>\n</View>",
                },
                {
                  title: "Text Component",
                  description: "Text is used to display any string content.",
                  keyPoints: [
                    "All text must be inside Text component",
                    "Supports nesting",
                    "Styled separately from View",
                  ],
                  code: "<Text>Welcome to React Native</Text>",
                },
                {
                  title: "Why HTML Tags Don’t Work",
                  description:
                    "HTML elements like div, p, span do not exist in React Native.",
                },
              ],
              commonMistakes: [
                {
                  mistake: "Using div or p in React Native",
                  consequence: "App crashes or fails to render",
                  fix: "Always use View and Text",
                },
              ],
              proTips: [
                "Think in native components, not HTML",
                "Every screen is built using View + Text",
                "Master these before moving forward",
              ],
            },
            tasks: [
              "Create a screen using only View and Text",
              "Nest multiple Views correctly",
            ],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Understand core components",
              "Avoid HTML mindset",
              "Build basic UI structure",
            ],
          },
          {
            day: 2,
            topic: "Image & SafeAreaView Components",
            subTopics: [
              "Image component",
              "Local vs remote images",
              "SafeAreaView",
              "Why safe area matters",
            ],
            explanation:
              "Learn how to display images and handle notches and system UI safely.",
            content: {
              introduction:
                "Images and safe layout handling are critical for professional mobile apps.",
              sections: [
                {
                  title: "Image Component",
                  description:
                    "Used to display images from local or remote sources.",
                  code: "<Image source={{ uri: 'https://example.com/img.png' }} />",
                },
                {
                  title: "SafeAreaView",
                  description:
                    "Ensures content does not overlap with notches or system bars.",
                  keyPoints: [
                    "Important for iOS",
                    "Prevents UI cut-off",
                    "Wrap main screen content",
                  ],
                  code: "<SafeAreaView>\n  <View />\n</SafeAreaView>",
                },
              ],
            },
            tasks: ["Render a remote image", "Wrap screen inside SafeAreaView"],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Handle images correctly",
              "Avoid UI overlap issues",
              "Build safe layouts",
            ],
          },
          {
            day: 3,
            topic: "ScrollView & Basic Layout Flow",
            subTopics: [
              "ScrollView",
              "Vertical scrolling",
              "When not to use ScrollView",
              "Content overflow",
            ],
            explanation:
              "Understand scrolling behavior and content flow in mobile apps.",
            content: {
              introduction:
                "Mobile screens are small. Scrolling is essential but must be used correctly.",
              sections: [
                {
                  title: "ScrollView",
                  description:
                    "Used for scrollable content with limited items.",
                  keyPoints: [
                    "Renders all children at once",
                    "Good for forms and static content",
                    "Not suitable for long lists",
                  ],
                  code: "<ScrollView>\n  <Text>Item</Text>\n</ScrollView>",
                },
              ],
            },
            tasks: [
              "Create a scrollable content screen",
              "Test overflow behavior",
            ],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Implement scrolling",
              "Choose correct container",
              "Understand performance basics",
            ],
          },
          {
            day: 4,
            topic: "StyleSheet & Styling Basics",
            subTopics: [
              "StyleSheet API",
              "Inline vs StyleSheet",
              "Common style properties",
            ],
            explanation:
              "Learn how styling works in React Native and why it differs from CSS.",
            content: {
              introduction:
                "React Native styling is inspired by CSS but uses JavaScript objects.",
              sections: [
                {
                  title: "StyleSheet.create",
                  description: "Used to define styles efficiently.",
                  code: "const styles = StyleSheet.create({\n  container: { padding: 16 }\n});",
                },
                {
                  title: "Styling Differences from CSS",
                  description: "Not all CSS properties are supported.",
                },
              ],
              commonMistakes: [
                {
                  mistake: "Using unsupported CSS properties",
                  consequence: "Styles don’t apply",
                  fix: "Use React Native style docs",
                },
              ],
            },
            tasks: [
              "Style a card component",
              "Move inline styles to StyleSheet",
            ],
            editorType: "javascript",
            estimatedTime: "30 minutes",
            objectives: [
              "Use StyleSheet correctly",
              "Write clean styles",
              "Understand styling limitations",
            ],
          },
          {
            day: 5,
            topic: "Flexbox Layout in React Native",
            subTopics: [
              "Flexbox default behavior",
              "flexDirection",
              "justifyContent",
              "alignItems",
            ],
            explanation:
              "Master layout positioning using Flexbox — the backbone of React Native UI.",
            content: {
              introduction: "All React Native layouts are built using Flexbox.",
              sections: [
                {
                  title: "Flexbox Defaults",
                  description:
                    "flexDirection is column by default in React Native.",
                },
                {
                  title: "Alignment Properties",
                  description: "Control spacing and alignment of elements.",
                },
              ],
            },
            tasks: [
              "Create row and column layouts",
              "Center content vertically and horizontally",
            ],
            editorType: "javascript",
            estimatedTime: "30 minutes",
            objectives: [
              "Understand Flexbox",
              "Build responsive layouts",
              "Avoid layout hacks",
            ],
          },
          {
            day: 6,
            topic: "Responsive UI & Dimensions",
            subTopics: [
              "Dimensions API",
              "Screen sizes",
              "Basic responsiveness",
              "Avoid fixed widths",
            ],
            explanation: "Build layouts that adapt to different screen sizes.",
            content: {
              sections: [
                {
                  title: "Dimensions API",
                  description: "Get screen width and height dynamically.",
                  code: "Dimensions.get('window').width",
                },
              ],
            },
            tasks: [
              "Create responsive container",
              "Test UI on different screen sizes",
            ],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Avoid hardcoded sizes",
              "Build adaptive UI",
              "Improve device compatibility",
            ],
          },
          {
            day: 7,
            topic: "Week 3 Review & UI Mini Project",
            subTopics: [
              "Core components",
              "Styling",
              "Flexbox",
              "Responsive layout",
            ],
            explanation:
              "Apply everything learned this week by building multiple static UI screens.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description:
                    "Build Home, Profile, and Settings screens using core components and styles.",
                },
              ],
            },
            tasks: [
              "Build Home screen UI",
              "Create Profile card layout",
              "Apply consistent styling",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Apply core RN concepts",
              "Build confidence with UI",
              "Prepare for lists and navigation",
            ],
          },
        ],
      },
      {
        week: 4,
        title: "Lists, FlatList & Reusable UI Patterns",
        weekGoal:
          "Learn how to render dynamic lists efficiently and build reusable UI patterns used in real apps",
        weekOutcome:
          "Build performant list-based screens using FlatList and reusable components",
        miniProject:
          "List-Based Screens – Feed Screen, Contacts List & Product List UI",
        canLearn: ["students", "beginners"],
        days: [
          {
            day: 1,
            topic: "Why Lists Matter in Mobile Apps",
            subTopics: [
              "Dynamic data rendering",
              "Scroll performance",
              "Real-world list examples",
              "ScrollView vs FlatList",
            ],
            explanation:
              "Understand why lists are central to almost every mobile app and why ScrollView is not suitable for large datasets.",
            content: {
              introduction:
                "Feeds, chats, contacts, products — most mobile apps are list-driven. Using the wrong component can crash or slow down your app.",
              sections: [
                {
                  title: "ScrollView vs FlatList",
                  description:
                    "ScrollView renders everything at once, while FlatList renders items lazily.",
                  keyPoints: [
                    "ScrollView is good for small static content",
                    "FlatList is optimized for large datasets",
                    "FlatList improves memory usage and performance",
                  ],
                },
                {
                  title: "Where Lists Are Used",
                  description:
                    "Almost every real app screen is a list in some form.",
                  examples: [
                    "Instagram feed",
                    "WhatsApp chats",
                    "E-commerce product list",
                    "Settings options",
                  ],
                },
              ],
              commonMistakes: [
                {
                  mistake: "Using ScrollView for long lists",
                  consequence: "App becomes slow or crashes",
                  fix: "Use FlatList instead",
                },
              ],
            },
            tasks: [
              "Identify list-based screens in popular apps",
              "Replace ScrollView with FlatList in a demo",
            ],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Understand list importance",
              "Choose correct list component",
              "Avoid performance issues",
            ],
          },
          {
            day: 2,
            topic: "FlatList Basics",
            subTopics: [
              "FlatList props",
              "data & renderItem",
              "keyExtractor",
              "Basic styling",
            ],
            explanation: "Learn how to render dynamic lists using FlatList.",
            content: {
              introduction:
                "FlatList is the backbone of dynamic UI in React Native.",
              sections: [
                {
                  title: "Basic FlatList Usage",
                  description: "Render a list using data and renderItem.",
                  code: "<FlatList data={data} renderItem={({ item }) => <Text>{item.name}</Text>} />",
                },
                {
                  title: "keyExtractor",
                  description: "Helps React identify list items uniquely.",
                  code: "keyExtractor={(item) => item.id}",
                },
              ],
            },
            tasks: ["Render a simple list using FlatList", "Add keyExtractor"],
            editorType: "javascript",
            estimatedTime: "30 minutes",
            objectives: [
              "Use FlatList correctly",
              "Avoid key warnings",
              "Render dynamic data",
            ],
          },
          {
            day: 3,
            topic: "Custom List Items & Reusable Components",
            subTopics: [
              "Custom item components",
              "Passing props to list items",
              "Reusable UI blocks",
            ],
            explanation:
              "Build clean and reusable list item components instead of inline JSX.",
            content: {
              introduction:
                "Professional apps never render list items inline — they use reusable components.",
              sections: [
                {
                  title: "Custom List Item Component",
                  description: "Extract list item UI into its own component.",
                },
                {
                  title: "Props in List Items",
                  description: "Pass item data and callbacks using props.",
                },
              ],
            },
            tasks: [
              "Create a reusable ListItem component",
              "Pass item data via props",
            ],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Improve code readability",
              "Reuse components",
              "Prepare for real projects",
            ],
          },
          {
            day: 4,
            topic: "List States – Empty, Loading & Error",
            subTopics: [
              "Empty list UI",
              "Loading indicators",
              "Error states",
              "Conditional rendering with FlatList",
            ],
            explanation:
              "Handle real-world list states gracefully for better UX.",
            content: {
              introduction:
                "A real app is never just a happy path. Lists must handle loading, empty, and error states.",
              sections: [
                {
                  title: "Empty State",
                  description: "Show meaningful UI when list has no data.",
                },
                {
                  title: "Loading State",
                  description: "Use loaders while fetching data.",
                },
                {
                  title: "Error State",
                  description: "Handle API or data failures cleanly.",
                },
              ],
            },
            tasks: [
              "Add empty state UI to FlatList",
              "Show loader conditionally",
            ],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Handle real-world scenarios",
              "Improve UX",
              "Build production-ready lists",
            ],
          },
          {
            day: 5,
            topic: "Basic FlatList Performance Optimization",
            subTopics: [
              "initialNumToRender",
              "windowSize",
              "removeClippedSubviews",
              "Why optimization matters",
            ],
            explanation:
              "Learn basic FlatList optimizations to keep apps smooth.",
            content: {
              introduction:
                "Unoptimized lists are one of the biggest causes of poor app performance.",
              sections: [
                {
                  title: "Performance Props",
                  description:
                    "Control how many items are rendered and kept in memory.",
                },
              ],
            },
            tasks: [
              "Tune FlatList performance props",
              "Test scrolling smoothness",
            ],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Understand list performance",
              "Prevent lag",
              "Think scalability",
            ],
          },
          {
            day: 6,
            topic: "Real-World List Patterns",
            subTopics: [
              "Feed layout",
              "Avatar + text rows",
              "Action buttons in lists",
            ],
            explanation:
              "Apply list knowledge to common real-world UI patterns.",
            content: {
              sections: [
                {
                  title: "Common List Patterns",
                  description:
                    "Learn patterns used in social and business apps.",
                },
              ],
            },
            tasks: [
              "Build a feed-style list item",
              "Add action button to list row",
            ],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Build realistic UI",
              "Prepare for navigation & APIs",
              "Increase confidence",
            ],
          },
          {
            day: 7,
            topic: "Week 4 Review & List-Based Mini Project",
            subTopics: [
              "FlatList",
              "Reusable components",
              "List states",
              "Basic optimization",
            ],
            explanation:
              "Build a complete list-driven screen combining all concepts from this week.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description:
                    "Build a Feed or Contacts screen using FlatList with empty, loading, and item components.",
                },
              ],
            },
            tasks: [
              "Build complete list screen",
              "Handle all list states",
              "Refactor for reusability",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Apply FlatList concepts",
              "Build real-world screens",
              "Get ready for navigation",
            ],
          },
        ],
      },
    ],
    outcome: "Can build basic screens and understand React Native fundamentals",
  },
  {
    phaseKey: "core-app",
    phase: 2,
    title: "Core App Development",
    level: "Beginner to Intermediate",
    startWeek: 5,
    endWeek: 8,
    weeks: [
      {
        week: 5,
        title: "Navigation Fundamentals & App Flow",
        weekGoal:
          "Understand how navigation works in React Native and design clean app flows",
        weekOutcome:
          "Build multi-screen apps with stack and tab navigation and a clear app flow",
        miniProject:
          "Multi-Screen App – Auth Flow, Home Tabs & Detail Screen Navigation",
        canLearn: ["students", "beginners"],
        days: [
          {
            day: 1,
            topic: "Introduction to Navigation in React Native",
            subTopics: [
              "What is navigation",
              "Why navigation is needed",
              "Popular navigation libraries",
              "React Navigation overview",
            ],
            explanation:
              "Understand why navigation is a core concept in mobile apps and how React Navigation solves it.",
            content: {
              introduction:
                "Unlike websites, mobile apps do not reload pages. Navigation manages how users move between screens inside a single running app.",
              sections: [
                {
                  title: "Why Navigation Matters",
                  description:
                    "Navigation defines the user journey inside an app.",
                  examples: [
                    "Login → Home → Profile",
                    "Feed → Post Details → Comments",
                  ],
                },
                {
                  title: "React Navigation",
                  description:
                    "The most widely used navigation library in React Native.",
                  keyPoints: [
                    "Declarative API",
                    "Supports multiple navigation patterns",
                    "Works on Android and iOS",
                  ],
                },
              ],
              commonMistakes: [
                {
                  mistake: "Trying to build navigation manually",
                  consequence: "Complex and buggy app flow",
                  fix: "Use React Navigation",
                },
              ],
            },
            tasks: [
              "Explore apps and identify navigation flows",
              "Install React Navigation dependencies",
            ],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Understand navigation concepts",
              "Know why React Navigation is used",
              "Prepare project for navigation",
            ],
          },
          {
            day: 2,
            topic: "Stack Navigation",
            subTopics: [
              "Stack navigator",
              "Screen registration",
              "Navigation actions",
              "Back behavior",
            ],
            explanation:
              "Learn how stack navigation works like a stack of screens.",
            content: {
              introduction:
                "Stack navigation is the most common pattern where screens are pushed and popped.",
              sections: [
                {
                  title: "Creating a Stack Navigator",
                  description: "Define screens and their order.",
                  code: "const Stack = createNativeStackNavigator();",
                },
                {
                  title: "Navigating Between Screens",
                  description:
                    "Move from one screen to another using navigation actions.",
                  code: "navigation.navigate('Details');",
                },
              ],
            },
            tasks: ["Create a stack navigator", "Navigate between two screens"],
            editorType: "javascript",
            estimatedTime: "30 minutes",
            objectives: [
              "Implement stack navigation",
              "Understand screen stack behavior",
              "Handle back navigation",
            ],
          },
          {
            day: 3,
            topic: "Passing Params & Route Data",
            subTopics: [
              "Passing parameters",
              "Route object",
              "Dynamic screens",
            ],
            explanation: "Pass data between screens and access it safely.",
            content: {
              introduction:
                "Most apps need to send data from one screen to another, such as IDs or user info.",
              sections: [
                {
                  title: "Passing Params",
                  description: "Send data while navigating.",
                  code: "navigation.navigate('Details', { id: 1 });",
                },
                {
                  title: "Accessing Route Params",
                  description: "Read data inside the destination screen.",
                },
              ],
            },
            tasks: [
              "Pass params between screens",
              "Display route data on screen",
            ],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Share data across screens",
              "Build dynamic navigation",
              "Avoid hardcoded screens",
            ],
          },
          {
            day: 4,
            topic: "Tab Navigation",
            subTopics: [
              "Bottom tabs",
              "Tab screens",
              "Icons and labels",
              "Tab-based app structure",
            ],
            explanation:
              "Learn how to create bottom tab navigation used in most modern apps.",
            content: {
              introduction:
                "Bottom tabs provide quick access to main sections of an app.",
              sections: [
                {
                  title: "Bottom Tab Navigator",
                  description: "Create a tab-based layout for main screens.",
                },
                {
                  title: "Tab vs Stack",
                  description:
                    "Tabs for top-level navigation, stacks for deeper flows.",
                },
              ],
            },
            tasks: ["Create bottom tab navigation", "Add multiple tab screens"],
            editorType: "javascript",
            estimatedTime: "30 minutes",
            objectives: [
              "Implement tab navigation",
              "Design main app structure",
              "Improve UX",
            ],
          },
          {
            day: 5,
            topic: "Auth Flow vs App Flow",
            subTopics: [
              "Authentication flow",
              "Conditional navigation",
              "Protected screens",
            ],
            explanation: "Separate login flow from main app flow cleanly.",
            content: {
              introduction:
                "Most apps have two flows: before login and after login.",
              sections: [
                {
                  title: "Auth Flow",
                  description: "Screens shown before user logs in.",
                },
                {
                  title: "App Flow",
                  description: "Screens accessible after authentication.",
                },
              ],
            },
            tasks: ["Create auth stack", "Switch between auth and app flow"],
            editorType: "javascript",
            estimatedTime: "30 minutes",
            objectives: [
              "Design secure app flow",
              "Control screen access",
              "Prepare for real authentication",
            ],
          },
          {
            day: 6,
            topic: "Navigation Best Practices",
            subTopics: [
              "Screen naming",
              "Navigation structure",
              "Avoiding deep nesting",
            ],
            explanation:
              "Learn how professionals structure navigation for maintainability.",
            content: {
              sections: [
                {
                  title: "Clean Navigation Structure",
                  description: "Organize navigators logically for scalability.",
                },
              ],
            },
            tasks: ["Refactor navigation structure", "Improve screen naming"],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Write maintainable navigation code",
              "Avoid navigation chaos",
              "Prepare for large apps",
            ],
          },
          {
            day: 7,
            topic: "Week 5 Review & Navigation Mini Project",
            subTopics: [
              "Stack navigation",
              "Tab navigation",
              "Auth flow",
              "Passing params",
            ],
            explanation:
              "Build a complete multi-screen app using proper navigation patterns.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description:
                    "Build an app with login flow, bottom tabs, and detail screens.",
                },
              ],
            },
            tasks: [
              "Build full navigation flow",
              "Test screen transitions",
              "Refactor navigation logic",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Apply navigation concepts",
              "Build real app flow",
              "Get ready for state management",
            ],
          },
        ],
      },
      {
        week: 6,
        title: "State Management & App Data Flow",
        weekGoal:
          "Understand how data flows in a React Native app and manage state cleanly at scale",
        weekOutcome:
          "Build predictable, maintainable apps using local state, Context API, and a modern lightweight store",
        miniProject:
          "State-Driven App – Auth State, Theme Toggle & Global User Data",
        canLearn: ["beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "Understanding State & Data Flow in React Native",
            subTopics: [
              "What is state",
              "Local vs global state",
              "Unidirectional data flow",
              "Common state problems",
            ],
            explanation:
              "Learn how data moves inside a React Native app and why poor state design causes bugs.",
            content: {
              introduction:
                "State controls everything users see. Bad state design leads to messy code, unnecessary re-renders, and unpredictable UI.",
              sections: [
                {
                  title: "Local vs Global State",
                  description: "Not all data should be global.",
                  keyPoints: [
                    "Local state belongs to a single component",
                    "Global state is shared across screens",
                    "Overusing global state causes complexity",
                  ],
                },
                {
                  title: "Unidirectional Data Flow",
                  description:
                    "Data flows from parent to child in one direction.",
                  analogy: "Like a one-way road — predictable and safe",
                },
              ],
              commonMistakes: [
                {
                  mistake: "Making everything global",
                  consequence: "Hard-to-debug app",
                  fix: "Start with local state, lift only when needed",
                },
              ],
            },
            tasks: [
              "Identify local vs global state in an app",
              "Refactor unnecessary global state",
            ],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Understand state types",
              "Design clean data flow",
              "Avoid overengineering",
            ],
          },
          {
            day: 2,
            topic: "Lifting State Up & Props Drilling",
            subTopics: [
              "Lifting state up",
              "Props drilling",
              "When props drilling is acceptable",
              "Warning signs",
            ],
            explanation:
              "Learn how to share state between components correctly.",
            content: {
              introduction:
                "Before jumping to global state, you must understand lifting state and its limits.",
              sections: [
                {
                  title: "Lifting State Up",
                  description:
                    "Move shared state to the nearest common parent.",
                },
                {
                  title: "Props Drilling",
                  description: "Passing props through multiple layers.",
                  keyPoints: [
                    "Acceptable for small depth",
                    "Problematic in deep trees",
                  ],
                },
              ],
            },
            tasks: [
              "Lift state to parent component",
              "Pass data through 2–3 levels",
            ],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Share state correctly",
              "Recognize props drilling issues",
              "Delay global state when possible",
            ],
          },
          {
            day: 3,
            topic: "Context API for Global State",
            subTopics: [
              "What is Context",
              "Creating context",
              "Provider & consumer",
              "When to use Context",
            ],
            explanation:
              "Use Context API to manage simple global state like auth and theme.",
            content: {
              introduction:
                "Context solves props drilling for global data but should be used carefully.",
              sections: [
                {
                  title: "Creating Context",
                  description: "Define shared data accessible across the app.",
                },
                {
                  title: "Using Provider",
                  description: "Wrap app with provider to expose state.",
                },
              ],
              commonMistakes: [
                {
                  mistake: "Putting too much logic in Context",
                  consequence: "Performance issues",
                  fix: "Keep Context small and focused",
                },
              ],
            },
            tasks: [
              "Create auth context",
              "Consume context in multiple screens",
            ],
            editorType: "javascript",
            estimatedTime: "30 minutes",
            objectives: [
              "Use Context API",
              "Avoid prop drilling",
              "Manage simple global state",
            ],
          },
          {
            day: 4,
            topic: "Modern Lightweight State Management (Zustand)",
            subTopics: [
              "Why not Redux everywhere",
              "Zustand basics",
              "Creating a store",
              "Using store in components",
            ],
            explanation:
              "Learn a modern, simple alternative to Redux used in many React Native apps.",
            content: {
              introduction:
                "Most apps don’t need Redux-level complexity. Lightweight stores solve real problems faster.",
              sections: [
                {
                  title: "Why Zustand",
                  description:
                    "Minimal boilerplate, simple API, great performance.",
                },
                {
                  title: "Store Basics",
                  description: "Create and use a shared store.",
                },
              ],
            },
            tasks: ["Create a Zustand store", "Share state across screens"],
            editorType: "javascript",
            estimatedTime: "30 minutes",
            objectives: [
              "Understand modern state tools",
              "Avoid overengineering",
              "Build scalable state logic",
            ],
          },
          {
            day: 5,
            topic: "Choosing the Right State Solution",
            subTopics: [
              "useState vs Context",
              "Context vs Zustand",
              "Decision guidelines",
              "Real-world examples",
            ],
            explanation:
              "Learn how to decide the right state tool for different scenarios.",
            content: {
              sections: [
                {
                  title: "Decision Framework",
                  description:
                    "Choose tools based on problem size, not popularity.",
                },
              ],
            },
            tasks: [
              "Classify state types in an app",
              "Choose correct solution for each",
            ],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Make correct architectural decisions",
              "Avoid unnecessary libraries",
              "Think like a senior developer",
            ],
          },
          {
            day: 6,
            topic: "State Performance & Re-render Optimization",
            subTopics: [
              "Why re-renders happen",
              "Avoid unnecessary updates",
              "State splitting",
            ],
            explanation: "Understand how state choices affect performance.",
            content: {
              sections: [
                {
                  title: "Reducing Re-renders",
                  description: "Smaller state = better performance.",
                },
              ],
            },
            tasks: ["Split large state objects", "Reduce unnecessary updates"],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Improve performance",
              "Write efficient components",
              "Prepare for production apps",
            ],
          },
          {
            day: 7,
            topic: "Week 6 Review & State Management Mini Project",
            subTopics: [
              "Local state",
              "Context API",
              "Zustand",
              "State decisions",
            ],
            explanation:
              "Build a small app demonstrating clean state management.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description:
                    "Build an app with auth state, theme toggle, and shared user data.",
                },
              ],
            },
            tasks: [
              "Implement auth state",
              "Add global theme toggle",
              "Refactor for clean data flow",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Apply state management concepts",
              "Build scalable logic",
              "Prepare for API integration",
            ],
          },
        ],
      },
      {
        week: 7,
        title: "Forms, Validation & User Input Handling",
        weekGoal:
          "Handle user input correctly using forms, validation, and controlled components",
        weekOutcome:
          "Build production-ready forms with proper validation and error handling",
        miniProject:
          "Auth Forms – Login, Signup & Profile Update with Validation",
        canLearn: ["students", "beginners"],
        days: [
          {
            day: 1,
            topic: "Understanding Forms & Controlled Inputs",
            subTopics: [
              "What are controlled inputs",
              "TextInput basics",
              "Keyboard handling",
              "Common input mistakes",
            ],
            explanation:
              "Learn how React Native handles user input and why controlled components are important.",
            content: {
              introduction:
                "Forms are the primary way users interact with apps. Incorrect input handling leads to bugs, crashes, and poor UX.",
              sections: [
                {
                  title: "Controlled Inputs",
                  description: "Input value is controlled by component state.",
                  keyPoints: [
                    "State is the single source of truth",
                    "UI updates when state changes",
                    "Predictable input behavior",
                  ],
                  code: "const [email, setEmail] = useState('');",
                },
                {
                  title: "TextInput Component",
                  description: "Core component for user input in React Native.",
                  keyPoints: [
                    "value and onChangeText are essential",
                    "Keyboard type improves UX",
                    "Auto-capitalization must be handled",
                  ],
                },
              ],
              commonMistakes: [
                {
                  mistake: "Using uncontrolled inputs",
                  consequence: "Inconsistent UI state",
                  fix: "Always bind input to state",
                },
              ],
            },
            tasks: [
              "Create controlled TextInput",
              "Handle email and password input",
            ],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Understand controlled inputs",
              "Handle user text correctly",
              "Avoid common input bugs",
            ],
          },
          {
            day: 2,
            topic: "Keyboard Handling & Input UX",
            subTopics: [
              "KeyboardAvoidingView",
              "Dismiss keyboard",
              "Input focus management",
            ],
            explanation:
              "Improve form UX by handling mobile keyboards properly.",
            content: {
              introduction:
                "Poor keyboard handling is one of the fastest ways to ruin form experience in mobile apps.",
              sections: [
                {
                  title: "KeyboardAvoidingView",
                  description: "Prevents keyboard from covering inputs.",
                },
                {
                  title: "Keyboard Dismiss",
                  description: "Close keyboard when user taps outside.",
                },
              ],
            },
            tasks: [
              "Wrap form with KeyboardAvoidingView",
              "Dismiss keyboard on background tap",
            ],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Improve input UX",
              "Handle keyboard correctly",
              "Build professional forms",
            ],
          },
          {
            day: 3,
            topic: "Form Validation Concepts",
            subTopics: [
              "Client-side validation",
              "Required fields",
              "Pattern validation",
              "Error messaging",
            ],
            explanation:
              "Learn why validation is necessary and how to guide users with clear errors.",
            content: {
              introduction:
                "Validation prevents bad data and improves user trust.",
              sections: [
                {
                  title: "Why Validation Matters",
                  description:
                    "Invalid data causes backend errors and poor UX.",
                },
                {
                  title: "Validation Types",
                  description: "Required, length, format, and custom rules.",
                },
              ],
            },
            tasks: ["Add required field validation", "Show error messages"],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Understand validation basics",
              "Prevent invalid input",
              "Improve form reliability",
            ],
          },
          {
            day: 4,
            topic: "react-hook-form for Performance Forms",
            subTopics: [
              "Why react-hook-form",
              "useForm hook",
              "Controller component",
            ],
            explanation:
              "Use react-hook-form to build scalable and performant forms.",
            content: {
              introduction:
                "react-hook-form reduces re-renders and simplifies validation logic.",
              sections: [
                {
                  title: "useForm Hook",
                  description:
                    "Centralized form management with minimal re-renders.",
                },
                {
                  title: "Controller",
                  description: "Connect controlled inputs to react-hook-form.",
                },
              ],
              commonMistakes: [
                {
                  mistake: "Manually managing every input state",
                  consequence: "Complex and slow forms",
                  fix: "Use react-hook-form",
                },
              ],
            },
            tasks: [
              "Build form using react-hook-form",
              "Control TextInput with Controller",
            ],
            editorType: "javascript",
            estimatedTime: "30 minutes",
            objectives: [
              "Use react-hook-form",
              "Reduce boilerplate",
              "Improve form performance",
            ],
          },
          {
            day: 5,
            topic: "Schema Validation with Yup / Zod",
            subTopics: [
              "Schema-based validation",
              "Yup basics",
              "Zod overview",
              "Error mapping",
            ],
            explanation: "Centralize validation rules using schemas.",
            content: {
              introduction:
                "Schema validation keeps rules consistent and reusable.",
              sections: [
                {
                  title: "Why Schema Validation",
                  description: "Single source of truth for form rules.",
                },
              ],
            },
            tasks: [
              "Create validation schema",
              "Integrate schema with react-hook-form",
            ],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Write clean validation logic",
              "Reuse rules across forms",
              "Avoid duplicated checks",
            ],
          },
          {
            day: 6,
            topic: "Handling Form Submission & Errors",
            subTopics: [
              "Submit handlers",
              "Disable submit button",
              "API error handling",
              "Loading state",
            ],
            explanation: "Handle real-world submission scenarios safely.",
            content: {
              sections: [
                {
                  title: "Submission Flow",
                  description: "Validate → submit → handle success or failure.",
                },
              ],
            },
            tasks: ["Disable submit while loading", "Show API error message"],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Handle real submission flows",
              "Prevent double submits",
              "Improve UX",
            ],
          },
          {
            day: 7,
            topic: "Week 7 Review & Forms Mini Project",
            subTopics: [
              "Controlled inputs",
              "Validation",
              "react-hook-form",
              "Schema validation",
            ],
            explanation:
              "Build complete auth and profile forms using best practices.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description:
                    "Build Login, Signup, and Profile Update forms with full validation.",
                },
              ],
            },
            tasks: [
              "Build login form",
              "Add signup validation",
              "Create profile update form",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Apply form handling skills",
              "Build production-ready forms",
              "Prepare for API integration",
            ],
          },
        ],
      },
      {
        week: 8,
        title: "API Integration, Networking & Data Handling",
        weekGoal:
          "Learn how to connect React Native apps with backend APIs and handle real-world data scenarios",
        weekOutcome:
          "Build API-driven screens with loading, error handling, and clean data flow",
        miniProject:
          "API-Driven App – Fetch Users, Details Screen & Pull-to-Refresh",
        canLearn: ["beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "How APIs Work & Networking Basics",
            subTopics: [
              "What is an API",
              "Client-server model",
              "HTTP methods",
              "JSON data format",
            ],
            explanation:
              "Understand how mobile apps communicate with servers and why APIs are central to modern apps.",
            content: {
              introduction:
                "Almost every mobile app talks to a backend. Understanding APIs helps you debug issues and design better app flows.",
              sections: [
                {
                  title: "Client–Server Communication",
                  description: "The app requests data, the server responds.",
                  keyPoints: [
                    "Client sends request",
                    "Server processes logic",
                    "Response returned as JSON",
                  ],
                  analogy:
                    "Like ordering food at a restaurant and receiving a dish",
                },
                {
                  title: "HTTP Methods",
                  description: "Different methods represent different actions.",
                  examples: [
                    "GET – fetch data",
                    "POST – create data",
                    "PUT/PATCH – update data",
                    "DELETE – remove data",
                  ],
                },
              ],
            },
            tasks: [
              "Inspect API responses in browser",
              "Identify HTTP methods in real apps",
            ],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Understand API basics",
              "Know client-server flow",
              "Read JSON responses",
            ],
          },
          {
            day: 2,
            topic: "Fetching Data Using fetch API",
            subTopics: [
              "fetch API",
              "GET requests",
              "Parsing JSON",
              "Basic error handling",
            ],
            explanation:
              "Learn how to fetch data from an API using native fetch.",
            content: {
              introduction:
                "Fetching data is the first step to making your app dynamic.",
              sections: [
                {
                  title: "Basic fetch Usage",
                  description: "Make a GET request and parse response.",
                  code: "const res = await fetch(url);\nconst data = await res.json();",
                },
                {
                  title: "Handling Errors",
                  description: "Always check response status.",
                },
              ],
            },
            tasks: ["Fetch data from public API", "Log response to console"],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Fetch remote data",
              "Handle responses",
              "Avoid silent failures",
            ],
          },
          {
            day: 3,
            topic: "Axios & API Abstraction",
            subTopics: [
              "Axios basics",
              "Axios vs fetch",
              "Creating API service layer",
            ],
            explanation:
              "Use Axios for cleaner syntax and better error handling.",
            content: {
              introduction:
                "Professional apps abstract API logic away from UI components.",
              sections: [
                {
                  title: "Axios Basics",
                  description: "Simplifies API calls.",
                  code: "axios.get('/users')",
                },
                {
                  title: "Service Layer",
                  description: "Centralize API logic for maintainability.",
                },
              ],
            },
            tasks: ["Install axios", "Create api.js service file"],
            editorType: "javascript",
            estimatedTime: "30 minutes",
            objectives: [
              "Use Axios",
              "Write cleaner API code",
              "Separate concerns",
            ],
          },
          {
            day: 4,
            topic: "Loading, Error & Empty States with APIs",
            subTopics: [
              "Loading indicators",
              "Error UI",
              "Empty data handling",
            ],
            explanation:
              "Handle real-world API states gracefully for better UX.",
            content: {
              introduction:
                "Real APIs fail, load slowly, or return empty data.",
              sections: [
                {
                  title: "Loading State",
                  description: "Show loader while data is fetching.",
                },
                {
                  title: "Error State",
                  description: "Display meaningful error messages.",
                },
              ],
            },
            tasks: ["Add loader while fetching", "Handle API error gracefully"],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Improve UX",
              "Handle failures",
              "Build resilient apps",
            ],
          },
          {
            day: 5,
            topic: "Pull to Refresh & Pagination Basics",
            subTopics: [
              "Pull-to-refresh",
              "Pagination concept",
              "Infinite scroll basics",
            ],
            explanation: "Enhance lists with refresh and pagination.",
            content: {
              sections: [
                {
                  title: "Pull to Refresh",
                  description: "Allow users to refresh data manually.",
                },
                {
                  title: "Pagination",
                  description: "Load data in chunks for performance.",
                },
              ],
            },
            tasks: [
              "Add pull-to-refresh to FlatList",
              "Simulate pagination logic",
            ],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Handle large data sets",
              "Improve performance",
              "Build modern UX",
            ],
          },
          {
            day: 6,
            topic: "API Security & Best Practices",
            subTopics: [
              "Environment variables",
              "API keys",
              "Avoid hardcoding secrets",
            ],
            explanation:
              "Protect sensitive data and follow API best practices.",
            content: {
              sections: [
                {
                  title: "Environment Variables",
                  description: "Store secrets securely.",
                },
              ],
            },
            tasks: ["Move API URL to env config", "Remove hardcoded values"],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Secure API usage",
              "Write production-ready code",
              "Follow best practices",
            ],
          },
          {
            day: 7,
            topic: "Week 8 Review & API Mini Project",
            subTopics: [
              "API fetching",
              "Axios",
              "List integration",
              "Loading & errors",
            ],
            explanation:
              "Build a complete API-driven screen using best practices.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description:
                    "Build a users list with detail screen using live API.",
                },
              ],
            },
            tasks: [
              "Fetch data from API",
              "Display list and details",
              "Handle loading and error states",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Apply API integration skills",
              "Build real-world screens",
              "Prepare for native features",
            ],
          },
        ],
      },
    ],
    outcome: "Can build real apps with APIs, auth and persistence",
  },
  {
    phaseKey: "quality-performance",
    phase: 3,
    title: "Quality, Performance & Stability",
    level: "Intermediate",
    startWeek: 9,
    endWeek: 13,
    weeks: [
      {
        week: 9,
        title: "Native Device APIs & Permissions",
        weekGoal:
          "Learn how to access native device features safely with correct permissions",
        weekOutcome:
          "Build features using camera, gallery, and device permissions without crashes",
        miniProject:
          "Media App – Camera Capture, Gallery Picker & Permission Handling",
        canLearn: ["beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "Understanding Native APIs & Permissions",
            subTopics: [
              "What are native APIs",
              "Why permissions are required",
              "Android vs iOS permission model",
              "User privacy basics",
            ],
            explanation:
              "Understand how React Native bridges JavaScript with native device capabilities and why permissions are mandatory.",
            content: {
              introduction:
                "Mobile devices expose powerful features like camera, storage, and location. Misusing permissions can crash apps or get them rejected from app stores.",
              sections: [
                {
                  title: "What are Native APIs?",
                  description:
                    "Native APIs provide access to device hardware and OS features.",
                  examples: ["Camera", "Gallery", "Contacts", "Location"],
                },
                {
                  title: "Why Permissions Matter",
                  description:
                    "Users must explicitly allow access to sensitive features.",
                  keyPoints: [
                    "Permissions protect user privacy",
                    "Denied permissions must be handled gracefully",
                    "Apps must explain why permission is needed",
                  ],
                },
              ],
              commonMistakes: [
                {
                  mistake: "Assuming permission is always granted",
                  consequence: "App crashes or broken features",
                  fix: "Always check and request permission",
                },
              ],
            },
            tasks: [
              "List permissions needed by common apps",
              "Identify permission flows in Android and iOS",
            ],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Understand native APIs",
              "Learn permission importance",
              "Respect user privacy",
            ],
          },
          {
            day: 2,
            topic: "Using Camera API",
            subTopics: [
              "Camera permissions",
              "Opening camera",
              "Capturing images",
              "Handling cancel actions",
            ],
            explanation:
              "Learn how to capture images using the device camera safely.",
            content: {
              introduction:
                "Camera access is one of the most common native features in mobile apps.",
              sections: [
                {
                  title: "Requesting Camera Permission",
                  description:
                    "Always request permission before opening the camera.",
                },
                {
                  title: "Capturing an Image",
                  description: "Open camera and handle the captured image.",
                },
              ],
            },
            tasks: [
              "Request camera permission",
              "Capture image and preview it",
            ],
            editorType: "javascript",
            estimatedTime: "30 minutes",
            objectives: [
              "Access camera safely",
              "Handle user cancel",
              "Avoid permission crashes",
            ],
          },
          {
            day: 3,
            topic: "Image Picker & Gallery Access",
            subTopics: [
              "Gallery permissions",
              "Picking images",
              "Handling large files",
              "Image compression basics",
            ],
            explanation:
              "Select images from device gallery and handle them efficiently.",
            content: {
              introduction:
                "Gallery selection is widely used for profile images and uploads.",
              sections: [
                {
                  title: "Opening Image Picker",
                  description: "Allow users to select images from gallery.",
                },
                {
                  title: "Handling Image Size",
                  description: "Large images must be optimized.",
                },
              ],
            },
            tasks: ["Pick image from gallery", "Display selected image"],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Use image picker",
              "Handle gallery permissions",
              "Prepare images for upload",
            ],
          },
          {
            day: 4,
            topic: "Permissions Handling Best Practices",
            subTopics: [
              "Permission states",
              "Denied vs blocked",
              "Fallback UI",
              "Re-request logic",
            ],
            explanation:
              "Handle all permission scenarios gracefully without frustrating users.",
            content: {
              introduction:
                "Permissions can be granted, denied, or permanently blocked. Your app must handle all cases.",
              sections: [
                {
                  title: "Permission States",
                  description:
                    "Understand granted, denied, and blocked states.",
                },
                {
                  title: "Fallback UI",
                  description: "Guide users when permission is denied.",
                },
              ],
            },
            tasks: ["Handle denied permission state", "Show fallback UI"],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Build user-friendly permission flows",
              "Avoid app store rejections",
              "Improve UX",
            ],
          },
          {
            day: 5,
            topic: "File Handling & Media Preview",
            subTopics: [
              "File paths",
              "Local URIs",
              "Previewing images",
              "Cleaning up resources",
            ],
            explanation:
              "Work with files returned from native APIs and display them safely.",
            content: {
              sections: [
                {
                  title: "Understanding File URIs",
                  description: "Handle local file paths correctly.",
                },
              ],
            },
            tasks: ["Preview selected media", "Clear temporary files"],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Handle files safely",
              "Avoid memory issues",
              "Prepare for uploads",
            ],
          },
          {
            day: 6,
            topic: "Common Native API Pitfalls",
            subTopics: [
              "Android permission quirks",
              "iOS permission prompts",
              "Crash scenarios",
              "Debugging native issues",
            ],
            explanation:
              "Understand common mistakes developers make with native APIs.",
            content: {
              sections: [
                {
                  title: "Platform Differences",
                  description:
                    "Android and iOS behave differently for permissions.",
                },
              ],
            },
            tasks: [
              "Test permission flow on both platforms",
              "Handle platform-specific edge cases",
            ],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Handle platform differences",
              "Reduce crashes",
              "Build stable features",
            ],
          },
          {
            day: 7,
            topic: "Week 9 Review & Media Mini Project",
            subTopics: ["Camera", "Gallery", "Permissions", "Media handling"],
            explanation:
              "Build a media-based feature using native APIs and permissions.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description:
                    "Build a profile image update feature using camera and gallery.",
                },
              ],
            },
            tasks: [
              "Implement camera capture",
              "Add gallery picker",
              "Handle permission fallback UI",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Apply native API concepts",
              "Build real-world feature",
              "Prepare for storage & uploads",
            ],
          },
        ],
      },
      {
        week: 10,
        title: "Local Storage, Persistence & Offline Experience",
        weekGoal:
          "Store data locally, persist app state, and build basic offline-friendly experiences",
        weekOutcome:
          "Use local storage solutions correctly and design apps that work with limited or no internet",
        miniProject:
          "Offline-Ready App – Persist Login, Cache Data & Restore App State",
        canLearn: ["beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "Why Local Storage Matters in Mobile Apps",
            subTopics: [
              "Offline-first mindset",
              "What data should be stored locally",
              "Temporary vs persistent data",
              "Common use cases",
            ],
            explanation:
              "Understand why mobile apps must store data locally and how it improves UX and reliability.",
            content: {
              introduction:
                "Mobile networks are unreliable. Local storage ensures your app still feels fast and usable even with poor or no internet.",
              sections: [
                {
                  title: "Offline-First Thinking",
                  description: "Design apps assuming network can fail anytime.",
                  examples: [
                    "Persisting login session",
                    "Saving user preferences",
                    "Caching API responses",
                  ],
                },
                {
                  title: "What Should Be Stored Locally",
                  description: "Not everything belongs in local storage.",
                  keyPoints: [
                    "Auth tokens and flags",
                    "User preferences",
                    "Non-sensitive cached data",
                  ],
                },
              ],
              commonMistakes: [
                {
                  mistake: "Storing sensitive data in plain storage",
                  consequence: "Security risk",
                  fix: "Use secure storage or backend validation",
                },
              ],
            },
            tasks: [
              "List data that should be stored locally in an app",
              "Identify data that should NOT be stored locally",
            ],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Understand offline needs",
              "Choose correct data to persist",
              "Think like a mobile user",
            ],
          },
          {
            day: 2,
            topic: "AsyncStorage Basics",
            subTopics: [
              "What is AsyncStorage",
              "Setting and getting values",
              "Storing JSON data",
              "Clearing storage",
            ],
            explanation:
              "Learn the most commonly used local storage solution in React Native.",
            content: {
              introduction:
                "AsyncStorage is simple key-value storage used for lightweight persistence.",
              sections: [
                {
                  title: "Saving Data",
                  description: "Store stringified values.",
                  code: "await AsyncStorage.setItem('token', value);",
                },
                {
                  title: "Reading Data",
                  description: "Retrieve stored values safely.",
                  code: "const token = await AsyncStorage.getItem('token');",
                },
              ],
            },
            tasks: ["Save user login flag", "Read data on app launch"],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Use AsyncStorage",
              "Persist simple data",
              "Restore state on app reload",
            ],
          },
          {
            day: 3,
            topic: "Structuring Stored Data & Namespacing",
            subTopics: [
              "Key naming conventions",
              "Storing objects safely",
              "Avoiding storage conflicts",
            ],
            explanation:
              "Organize local storage data to avoid bugs and collisions.",
            content: {
              introduction:
                "Messy storage keys lead to hard-to-debug issues in large apps.",
              sections: [
                {
                  title: "Key Naming Strategy",
                  description: "Use clear, namespaced keys.",
                },
                {
                  title: "Storing Objects",
                  description: "Always stringify and parse JSON.",
                },
              ],
            },
            tasks: [
              "Refactor storage keys using namespace",
              "Store and retrieve object data",
            ],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Write maintainable storage logic",
              "Avoid key conflicts",
              "Handle structured data",
            ],
          },
          {
            day: 4,
            topic: "MMKV & High-Performance Storage",
            subTopics: [
              "Why AsyncStorage is sometimes slow",
              "MMKV overview",
              "When to use MMKV",
            ],
            explanation:
              "Learn about faster storage alternatives for performance-critical data.",
            content: {
              introduction:
                "As apps grow, storage performance becomes important.",
              sections: [
                {
                  title: "MMKV Storage",
                  description:
                    "High-performance key-value storage backed by native implementation.",
                },
                {
                  title: "When to Use MMKV",
                  description:
                    "Use for frequently accessed data like auth flags.",
                },
              ],
            },
            tasks: [
              "Compare AsyncStorage vs MMKV use cases",
              "Store auth flag using MMKV",
            ],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Understand storage performance",
              "Choose correct storage tool",
              "Build scalable apps",
            ],
          },
          {
            day: 5,
            topic: "Offline Caching Strategies",
            subTopics: [
              "Cache-first vs network-first",
              "Stale data handling",
              "Basic cache invalidation",
            ],
            explanation:
              "Design simple caching strategies for better offline experience.",
            content: {
              sections: [
                {
                  title: "Caching Strategies",
                  description: "Decide when to read from cache vs API.",
                },
              ],
            },
            tasks: [
              "Implement simple cache logic",
              "Show cached data when offline",
            ],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Design offline-friendly apps",
              "Reduce unnecessary API calls",
              "Improve perceived performance",
            ],
          },
          {
            day: 6,
            topic: "Restoring App State on Launch",
            subTopics: [
              "Splash loading",
              "Hydrating state",
              "Navigation restoration",
            ],
            explanation:
              "Restore user session and app state when the app restarts.",
            content: {
              sections: [
                {
                  title: "App Hydration",
                  description: "Load stored data before rendering main UI.",
                },
              ],
            },
            tasks: [
              "Restore login state on launch",
              "Show splash while loading storage",
            ],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Build smooth app startup",
              "Avoid flickers",
              "Persist user experience",
            ],
          },
          {
            day: 7,
            topic: "Week 10 Review & Offline Mini Project",
            subTopics: ["AsyncStorage", "MMKV", "Caching", "State restoration"],
            explanation:
              "Build an offline-capable app feature using local storage and caching.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description:
                    "Build an app that persists login, caches data, and restores state on restart.",
                },
              ],
            },
            tasks: [
              "Persist user session",
              "Cache API data",
              "Restore state on relaunch",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Apply local storage concepts",
              "Build offline-friendly app",
              "Prepare for performance optimization",
            ],
          },
        ],
      },
      {
        week: 11,
        title: "Performance Optimization & Rendering Best Practices",
        weekGoal:
          "Understand how rendering works in React Native and optimize apps for smooth performance",
        weekOutcome:
          "Build fast, responsive screens by reducing re-renders and optimizing heavy UI",
        miniProject:
          "Performance Tune-Up – Optimize Feed Screen & Fix Jank Issues",
        canLearn: ["professionals"],
        days: [
          {
            day: 1,
            topic: "How Rendering Works in React Native",
            subTopics: [
              "JS thread vs UI thread",
              "Re-render cycle",
              "What triggers re-renders",
              "Why performance issues happen",
            ],
            explanation:
              "Understand how React Native renders UI and why unnecessary re-renders cause lag.",
            content: {
              introduction:
                "Most performance issues are caused by poor understanding of how rendering works rather than device limitations.",
              sections: [
                {
                  title: "JS Thread & UI Thread",
                  description:
                    "React Native uses separate threads for logic and UI.",
                  keyPoints: [
                    "JS thread runs business logic",
                    "UI thread handles rendering",
                    "Blocking JS affects UI responsiveness",
                  ],
                  analogy:
                    "Like a chef (JS) and waiter (UI) — if chef is slow, service suffers",
                },
                {
                  title: "Re-render Cycle",
                  description:
                    "Components re-render when props or state change.",
                },
              ],
            },
            tasks: [
              "Identify re-render triggers in a component",
              "Add console logs to observe re-renders",
            ],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Understand rendering internals",
              "Identify performance bottlenecks",
              "Think beyond UI code",
            ],
          },
          {
            day: 2,
            topic: "Memoization with React.memo & useCallback",
            subTopics: [
              "React.memo",
              "useCallback",
              "useMemo basics",
              "When memoization helps",
            ],
            explanation:
              "Prevent unnecessary re-renders using memoization techniques.",
            content: {
              introduction:
                "Memoization avoids recomputing values and re-rendering components unnecessarily.",
              sections: [
                {
                  title: "React.memo",
                  description:
                    "Memoize functional components to prevent re-render when props don’t change.",
                },
                {
                  title: "useCallback & useMemo",
                  description: "Cache functions and values across renders.",
                },
              ],
              commonMistakes: [
                {
                  mistake: "Overusing memo everywhere",
                  consequence: "Complex code without real benefit",
                  fix: "Measure first, optimize later",
                },
              ],
            },
            tasks: [
              "Wrap a component with React.memo",
              "Use useCallback for event handlers",
            ],
            editorType: "javascript",
            estimatedTime: "30 minutes",
            objectives: [
              "Reduce unnecessary renders",
              "Use memoization correctly",
              "Write efficient components",
            ],
          },
          {
            day: 3,
            topic: "Optimizing FlatList & Large Lists",
            subTopics: [
              "Why lists cause jank",
              "getItemLayout",
              "keyExtractor best practices",
              "Avoiding inline functions",
            ],
            explanation: "Optimize list-heavy screens for smooth scrolling.",
            content: {
              introduction:
                "Most performance complaints in apps come from poorly optimized lists.",
              sections: [
                {
                  title: "List Optimization Techniques",
                  description: "Reduce work per list item render.",
                },
              ],
            },
            tasks: [
              "Optimize FlatList props",
              "Remove inline renderItem functions",
            ],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Improve scrolling performance",
              "Optimize large datasets",
              "Build scalable feeds",
            ],
          },
          {
            day: 4,
            topic: "Avoiding Expensive Computations in Render",
            subTopics: [
              "Heavy calculations",
              "Derived state",
              "Moving logic outside render",
            ],
            explanation:
              "Prevent blocking UI by avoiding heavy logic inside render cycle.",
            content: {
              sections: [
                {
                  title: "Expensive Operations",
                  description:
                    "Heavy loops or JSON parsing inside render cause lag.",
                },
              ],
            },
            tasks: ["Move heavy logic out of render", "Use memoized selectors"],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Keep renders lightweight",
              "Improve UI responsiveness",
              "Write production-ready code",
            ],
          },
          {
            day: 5,
            topic: "Image & Asset Performance",
            subTopics: [
              "Large images",
              "Image caching",
              "Lazy loading",
              "Why images slow apps",
            ],
            explanation:
              "Optimize images to avoid memory and performance issues.",
            content: {
              sections: [
                {
                  title: "Image Optimization",
                  description: "Reduce size and load images efficiently.",
                },
              ],
            },
            tasks: [
              "Replace heavy images with optimized versions",
              "Implement basic image caching",
            ],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Reduce memory usage",
              "Improve load times",
              "Avoid image-related crashes",
            ],
          },
          {
            day: 6,
            topic: "Measuring Performance & Debugging",
            subTopics: [
              "Performance monitor",
              "Why-did-you-render",
              "Profiling basics",
            ],
            explanation: "Measure performance instead of guessing.",
            content: {
              sections: [
                {
                  title: "Profiling Tools",
                  description: "Use built-in tools to inspect performance.",
                },
              ],
            },
            tasks: ["Enable performance monitor", "Identify slow components"],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Debug performance issues",
              "Use profiling tools",
              "Optimize with confidence",
            ],
          },
          {
            day: 7,
            topic: "Week 11 Review & Performance Mini Project",
            subTopics: [
              "Rendering",
              "Memoization",
              "List optimization",
              "Profiling",
            ],
            explanation:
              "Optimize an existing screen and measure improvements.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description:
                    "Take a slow feed screen and optimize it for smooth performance.",
                },
              ],
            },
            tasks: [
              "Profile performance before changes",
              "Apply optimizations",
              "Compare before vs after",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Apply performance concepts",
              "Build smooth UI",
              "Think like a senior engineer",
            ],
          },
        ],
      },
      {
        week: 12,
        title: "Animations, Gestures & Smooth UI Interactions",
        weekGoal:
          "Create smooth, interactive user experiences using animations and gestures",
        weekOutcome:
          "Build fluid animations and gesture-driven UI without jank",
        miniProject:
          "Interactive UI – Animated Cards, Swipe Gestures & Micro-interactions",
        canLearn: ["beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "Why Animations Matter in Mobile Apps",
            subTopics: [
              "User experience impact",
              "Micro-interactions",
              "Good vs bad animations",
              "Performance considerations",
            ],
            explanation:
              "Understand how animations improve UX and when they hurt performance.",
            content: {
              introduction:
                "Animations guide users, provide feedback, and make apps feel alive. Overdone or poorly implemented animations can make apps feel slow.",
              sections: [
                {
                  title: "Purpose of Animations",
                  description:
                    "Animations communicate state changes and user feedback.",
                  examples: [
                    "Button press feedback",
                    "Screen transitions",
                    "Loading indicators",
                  ],
                },
                {
                  title: "Good vs Bad Animations",
                  description:
                    "Subtle and fast animations enhance UX; long and blocking animations frustrate users.",
                },
              ],
            },
            tasks: [
              "Identify animations in popular apps",
              "Classify animations as helpful or distracting",
            ],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Understand animation purpose",
              "Think UX-first",
              "Avoid over-animating UI",
            ],
          },
          {
            day: 2,
            topic: "Animated API Basics",
            subTopics: [
              "Animated.Value",
              "Timing animations",
              "useNativeDriver",
              "Simple transitions",
            ],
            explanation:
              "Learn the core Animated API provided by React Native.",
            content: {
              introduction:
                "Animated API is the foundation for basic animations in React Native.",
              sections: [
                {
                  title: "Animated.Value",
                  description:
                    "Represents a value that can be animated over time.",
                },
                {
                  title: "Timing Animations",
                  description: "Animate values using duration and easing.",
                },
              ],
            },
            tasks: [
              "Animate opacity of a component",
              "Create a simple fade-in effect",
            ],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Use Animated API",
              "Create basic animations",
              "Understand native driver",
            ],
          },
          {
            day: 3,
            topic: "Layout Animations & Transitions",
            subTopics: [
              "LayoutAnimation",
              "Animating height & width",
              "Auto layout transitions",
            ],
            explanation:
              "Animate layout changes smoothly without manual calculations.",
            content: {
              introduction:
                "LayoutAnimation animates layout changes automatically when views update.",
              sections: [
                {
                  title: "Using LayoutAnimation",
                  description: "Enable animations for layout changes.",
                },
              ],
            },
            tasks: [
              "Animate expand/collapse section",
              "Apply LayoutAnimation to list changes",
            ],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Animate layout changes",
              "Reduce manual animation logic",
              "Improve UI smoothness",
            ],
          },
          {
            day: 4,
            topic: "Gesture Handling Basics",
            subTopics: [
              "What are gestures",
              "Tap vs swipe",
              "Gesture Handler overview",
            ],
            explanation:
              "Understand how gestures work and why native gesture handling is required.",
            content: {
              introduction:
                "Gestures are more complex than clicks and need native handling for performance.",
              sections: [
                {
                  title: "Gesture Types",
                  description: "Tap, long press, swipe, pan, and pinch.",
                },
              ],
            },
            tasks: [
              "Implement basic swipe gesture",
              "Handle tap vs long press",
            ],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Understand gestures",
              "Use gesture handler",
              "Avoid touch conflicts",
            ],
          },
          {
            day: 5,
            topic: "Reanimated Basics for Smooth Animations",
            subTopics: [
              "Why Reanimated",
              "Shared values",
              "Worklets",
              "UI thread animations",
            ],
            explanation:
              "Use Reanimated for high-performance animations that run on the UI thread.",
            content: {
              introduction:
                "Reanimated enables smooth animations without blocking the JS thread.",
              sections: [
                {
                  title: "Shared Values",
                  description: "Values that live on the UI thread.",
                },
                {
                  title: "Worklets",
                  description: "Code that runs directly on the UI thread.",
                },
              ],
            },
            tasks: [
              "Create a Reanimated shared value",
              "Animate transform property",
            ],
            editorType: "javascript",
            estimatedTime: "30 minutes",
            objectives: [
              "Understand Reanimated concepts",
              "Build jank-free animations",
              "Prepare for advanced gestures",
            ],
          },
          {
            day: 6,
            topic: "Combining Gestures with Animations",
            subTopics: [
              "Pan gestures",
              "Swipe-to-dismiss",
              "Gesture-driven animations",
            ],
            explanation:
              "Combine gestures and animations for interactive UI patterns.",
            content: {
              sections: [
                {
                  title: "Gesture + Animation Flow",
                  description: "User input directly controls animation values.",
                },
              ],
            },
            tasks: [
              "Build swipe-to-dismiss card",
              "Animate card based on gesture",
            ],
            editorType: "javascript",
            estimatedTime: "30–35 minutes",
            objectives: [
              "Build interactive UI",
              "Use gesture-driven animations",
              "Create modern UX patterns",
            ],
          },
          {
            day: 7,
            topic: "Week 12 Review & Animation Mini Project",
            subTopics: [
              "Animated API",
              "Reanimated",
              "Gestures",
              "Micro-interactions",
            ],
            explanation: "Build a fully interactive animated UI feature.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description:
                    "Build an animated card stack with swipe gestures and smooth transitions.",
                },
              ],
            },
            tasks: [
              "Implement animated cards",
              "Add gesture-based interactions",
              "Optimize animation performance",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Apply animation concepts",
              "Build smooth interactions",
              "Prepare for production-grade UI",
            ],
          },
        ],
      },
      {
        week: 13,
        title: "Testing, Debugging & App Quality",
        weekGoal:
          "Ensure app stability and confidence by learning testing, debugging, and quality practices",
        weekOutcome:
          "Write testable components, debug faster, and ship more reliable React Native apps",
        miniProject: "Quality Sprint – Test Critical Screens & Fix Real Bugs",
        canLearn: ["beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "Debugging Mindset & Tools",
            subTopics: [
              "Common React Native bugs",
              "Reading stack traces",
              "console.log vs debugger",
              "Flipper overview",
            ],
            explanation:
              "Learn how to debug issues systematically instead of guessing.",
            content: {
              introduction:
                "Good developers don’t avoid bugs — they debug them efficiently. A strong debugging mindset saves hours.",
              sections: [
                {
                  title: "Debugging Workflow",
                  description: "Reproduce → isolate → fix → verify.",
                  keyPoints: [
                    "Always reproduce the bug",
                    "Reduce the problem scope",
                    "Verify fix with tests",
                  ],
                },
                {
                  title: "Flipper & DevTools",
                  description:
                    "Inspect logs, network, and layout using Flipper.",
                },
              ],
              commonMistakes: [
                {
                  mistake: "Randomly changing code",
                  consequence: "Introduces new bugs",
                  fix: "Follow a structured debugging process",
                },
              ],
            },
            tasks: [
              "Reproduce a simple bug intentionally",
              "Debug using logs and Flipper",
            ],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Adopt debugging mindset",
              "Use proper tools",
              "Reduce debugging time",
            ],
          },
          {
            day: 2,
            topic: "Introduction to Testing in React Native",
            subTopics: [
              "Why testing matters",
              "Types of tests",
              "What to test vs what not to test",
            ],
            explanation:
              "Understand testing fundamentals and where tests add real value.",
            content: {
              introduction:
                "Tests protect your app from regressions and give confidence while refactoring.",
              sections: [
                {
                  title: "Types of Tests",
                  description: "Unit, integration, and end-to-end tests.",
                  examples: [
                    "Unit: utility functions",
                    "Integration: component behavior",
                    "E2E: user flows",
                  ],
                },
              ],
            },
            tasks: [
              "Identify testable parts of an app",
              "Decide which tests add most value",
            ],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Understand testing value",
              "Avoid over-testing",
              "Think pragmatically",
            ],
          },
          {
            day: 3,
            topic: "Unit Testing with Jest",
            subTopics: [
              "Jest setup",
              "Writing unit tests",
              "Mocking functions",
              "Test assertions",
            ],
            explanation: "Write unit tests for logic and utilities using Jest.",
            content: {
              introduction:
                "Unit tests validate small pieces of logic quickly and reliably.",
              sections: [
                {
                  title: "Basic Jest Test",
                  description: "Structure of a unit test.",
                  code: "test('adds numbers', () => { expect(add(1,2)).toBe(3); });",
                },
              ],
            },
            tasks: [
              "Write unit test for utility function",
              "Mock a simple dependency",
            ],
            editorType: "javascript",
            estimatedTime: "30 minutes",
            objectives: [
              "Write Jest tests",
              "Test pure logic",
              "Build testing confidence",
            ],
          },
          {
            day: 4,
            topic: "Component Testing with Testing Library",
            subTopics: [
              "React Native Testing Library",
              "Rendering components",
              "Querying elements",
              "User interactions",
            ],
            explanation: "Test components the way users interact with them.",
            content: {
              introduction:
                "Component tests focus on behavior, not implementation details.",
              sections: [
                {
                  title: "Rendering Components",
                  description: "Render components and query UI elements.",
                },
              ],
            },
            tasks: ["Test button press behavior", "Assert text rendering"],
            editorType: "javascript",
            estimatedTime: "30 minutes",
            objectives: [
              "Test UI behavior",
              "Write user-centric tests",
              "Avoid brittle tests",
            ],
          },
          {
            day: 5,
            topic: "Mocking APIs & Navigation",
            subTopics: [
              "Mocking network requests",
              "Mocking navigation",
              "Isolating components",
            ],
            explanation:
              "Test components without relying on real APIs or navigation.",
            content: {
              sections: [
                {
                  title: "Mocking Strategies",
                  description: "Control external dependencies in tests.",
                },
              ],
            },
            tasks: ["Mock API response", "Test loading and error states"],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Isolate tests",
              "Test edge cases",
              "Increase reliability",
            ],
          },
          {
            day: 6,
            topic: "Error Handling & Crash Prevention",
            subTopics: [
              "try/catch patterns",
              "Graceful fallbacks",
              "Global error boundaries",
            ],
            explanation:
              "Prevent crashes and handle unexpected failures gracefully.",
            content: {
              sections: [
                {
                  title: "Error Boundaries",
                  description: "Catch rendering errors and show fallback UI.",
                },
              ],
            },
            tasks: ["Add error boundary", "Handle async errors safely"],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Prevent crashes",
              "Improve stability",
              "Build resilient apps",
            ],
          },
          {
            day: 7,
            topic: "Week 13 Review & Quality Mini Project",
            subTopics: [
              "Debugging",
              "Unit tests",
              "Component tests",
              "Error handling",
            ],
            explanation:
              "Improve quality of an existing app by adding tests and fixing bugs.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description:
                    "Add tests to critical screens and fix reported bugs.",
                },
              ],
            },
            tasks: [
              "Add unit and component tests",
              "Fix identified bugs",
              "Refactor with confidence",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Apply testing practices",
              "Ship higher-quality code",
              "Prepare for deployment",
            ],
          },
        ],
      },
    ],
    outcome: "Build smooth, stable and testable applications",
  },
  {
    phaseKey: "production-release",
    phase: 4,
    title: "Production, Architecture & Release",
    level: "Intermediate to Advanced",
    startWeek: 14,
    endWeek: 16,
    weeks: [
      {
        week: 14,
        title: "Build, Release & App Store Deployment",
        weekGoal:
          "Understand how to prepare, build, and release React Native apps to Play Store and App Store",
        weekOutcome:
          "Generate production builds, handle configs, and confidently submit apps to stores",
        miniProject:
          "Release Sprint – Generate APK/AAB & iOS Archive with Store-Ready Config",
        canLearn: ["professionals"],
        days: [
          {
            day: 1,
            topic: "Release Mindset & Production Checklist",
            subTopics: [
              "Development vs production builds",
              "Release checklist",
              "Environment configs",
              "Common release mistakes",
            ],
            explanation:
              "Learn what changes when an app moves from development to production.",
            content: {
              introduction:
                "Many apps work perfectly in development but fail during release due to missing configs or bad assumptions.",
              sections: [
                {
                  title: "Production Readiness",
                  description:
                    "Release builds are optimized, minified, and stricter than dev builds.",
                  keyPoints: [
                    "No dev tools",
                    "Optimized JS bundle",
                    "Strict permission and config checks",
                  ],
                },
                {
                  title: "Release Checklist",
                  description: "Things you must verify before building.",
                  examples: [
                    "API URLs",
                    "App icons",
                    "Splash screen",
                    "Version number",
                  ],
                },
              ],
            },
            tasks: [
              "Create a personal release checklist",
              "Identify dev-only configs in project",
            ],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Understand production differences",
              "Avoid last-minute failures",
              "Think release-first",
            ],
          },
          {
            day: 2,
            topic: "Android Release Build (APK & AAB)",
            subTopics: [
              "Debug vs release build",
              "Keystore generation",
              "APK vs AAB",
              "Gradle configs",
            ],
            explanation: "Build signed Android release apps correctly.",
            content: {
              introduction:
                "Android release builds require signing and correct Gradle configuration.",
              sections: [
                {
                  title: "Keystore & Signing",
                  description: "Every Android app must be signed for release.",
                },
                {
                  title: "APK vs AAB",
                  description: "AAB is required for Play Store uploads.",
                },
              ],
            },
            tasks: ["Generate release keystore", "Build signed AAB"],
            editorType: "bash",
            estimatedTime: "30 minutes",
            objectives: [
              "Generate Android release build",
              "Understand signing",
              "Prepare for Play Store",
            ],
          },
          {
            day: 3,
            topic: "iOS Release Build & Certificates",
            subTopics: [
              "iOS certificates",
              "Provisioning profiles",
              "Xcode archive",
              "App Store Connect basics",
            ],
            explanation:
              "Prepare and archive iOS apps for App Store submission.",
            content: {
              introduction:
                "iOS release requires correct certificates and profiles managed via Apple Developer account.",
              sections: [
                {
                  title: "Certificates & Profiles",
                  description:
                    "Authorize your app to run on devices and App Store.",
                },
                {
                  title: "Xcode Archive",
                  description: "Create distributable build using Xcode.",
                },
              ],
            },
            tasks: ["Configure bundle identifier", "Archive app using Xcode"],
            editorType: "objective-c",
            estimatedTime: "30–35 minutes",
            objectives: [
              "Understand iOS signing",
              "Create iOS archive",
              "Prepare for App Store",
            ],
          },
          {
            day: 4,
            topic: "Environment Variables & Build Configs",
            subTopics: [
              "Dev vs prod env",
              "API switching",
              "Secrets handling",
              "Build-time configs",
            ],
            explanation: "Manage multiple environments safely.",
            content: {
              sections: [
                {
                  title: "Environment Strategy",
                  description: "Switch configs without changing code.",
                },
              ],
            },
            tasks: ["Configure prod API URL", "Remove hardcoded secrets"],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Secure builds",
              "Avoid prod data leaks",
              "Prepare scalable setup",
            ],
          },
          {
            day: 5,
            topic: "App Store & Play Store Requirements",
            subTopics: [
              "Store guidelines",
              "Privacy policy",
              "Permissions justification",
              "Metadata preparation",
            ],
            explanation: "Understand store rules to avoid rejection.",
            content: {
              introduction:
                "Most rejections are due to missing metadata or unclear permission usage.",
              sections: [
                {
                  title: "Store Guidelines",
                  description: "Each store has strict policies.",
                },
              ],
            },
            tasks: [
              "Write basic app description",
              "Draft privacy policy outline",
            ],
            editorType: "text",
            estimatedTime: "25 minutes",
            objectives: [
              "Avoid store rejection",
              "Prepare compliance docs",
              "Ship responsibly",
            ],
          },
          {
            day: 6,
            topic: "Versioning, Updates & Rollbacks",
            subTopics: [
              "Version numbers",
              "Build numbers",
              "Hotfix strategy",
              "Rollback basics",
            ],
            explanation: "Manage app updates safely after release.",
            content: {
              sections: [
                {
                  title: "Versioning Strategy",
                  description: "Semantic versioning for mobile apps.",
                },
              ],
            },
            tasks: ["Update version code", "Simulate release notes"],
            editorType: "javascript",
            estimatedTime: "20 minutes",
            objectives: [
              "Manage releases",
              "Plan updates",
              "Reduce release risk",
            ],
          },
          {
            day: 7,
            topic: "Week 14 Review & Release Mini Project",
            subTopics: [
              "Android build",
              "iOS build",
              "Env configs",
              "Store prep",
            ],
            explanation: "Simulate a real app release end-to-end.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description:
                    "Prepare a demo app and generate store-ready builds.",
                },
              ],
            },
            tasks: [
              "Generate Android AAB",
              "Archive iOS build",
              "Prepare store assets",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Ship confidently",
              "Understand release flow",
              "Be industry-ready",
            ],
          },
        ],
      },
      {
        week: 15,
        title: "Advanced Architecture, Code Organization & Scalability",
        weekGoal:
          "Design scalable React Native applications with clean architecture and maintainable codebase",
        weekOutcome:
          "Refactor apps into scalable structure ready for long-term growth",
        miniProject:
          "Architecture Refactor – Convert a Medium App into Scalable Structure",
        canLearn: ["professionals"],
        days: [
          {
            day: 1,
            topic: "Thinking in App Architecture",
            subTopics: [
              "Why architecture matters",
              "Small app vs large app mindset",
              "Technical debt basics",
              "Scaling problems",
            ],
            explanation:
              "Understand why poor architecture slows teams and how early decisions affect scalability.",
            content: {
              introduction:
                "Most apps fail to scale not because of features, but because of messy architecture that becomes unmaintainable.",
              sections: [
                {
                  title: "What is App Architecture?",
                  description:
                    "How code, data, and responsibilities are structured.",
                  analogy:
                    "Like city planning — bad roads cause traffic forever",
                },
                {
                  title: "When Architecture Becomes Critical",
                  description: "As screens, developers, and features grow.",
                },
              ],
            },
            tasks: [
              "Analyze folder structure of a real app",
              "Identify architectural problems",
            ],
            editorType: "text",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Think long-term",
              "Understand scalability issues",
              "Avoid technical debt",
            ],
          },
          {
            day: 2,
            topic: "Project Folder Structure & Module Boundaries",
            subTopics: [
              "Feature-based structure",
              "Shared vs feature code",
              "Avoiding deep nesting",
            ],
            explanation:
              "Organize code so that features are isolated and easy to manage.",
            content: {
              sections: [
                {
                  title: "Feature-Based Architecture",
                  description: "Group code by feature, not by type.",
                },
              ],
            },
            tasks: [
              "Refactor flat structure into feature-based",
              "Move shared components correctly",
            ],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Improve code organization",
              "Reduce coupling",
              "Prepare for team scaling",
            ],
          },
          {
            day: 3,
            topic: "Separation of Concerns",
            subTopics: [
              "UI vs business logic",
              "Service layers",
              "Hooks for logic",
            ],
            explanation:
              "Separate responsibilities to make code testable and reusable.",
            content: {
              sections: [
                {
                  title: "Why Separation Matters",
                  description: "Mixing logic and UI causes brittle code.",
                },
              ],
            },
            tasks: [
              "Extract logic into custom hooks",
              "Move API calls to services",
            ],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Write clean code",
              "Improve testability",
              "Reduce complexity",
            ],
          },
          {
            day: 4,
            topic: "Custom Hooks & Reusable Logic",
            subTopics: [
              "Creating custom hooks",
              "Rules of hooks",
              "Common hook patterns",
            ],
            explanation: "Reuse logic across components using custom hooks.",
            content: {
              sections: [
                {
                  title: "Custom Hook Pattern",
                  description: "Encapsulate logic cleanly.",
                },
              ],
            },
            tasks: [
              "Build a useAuth hook",
              "Create reusable data-fetching hook",
            ],
            editorType: "javascript",
            estimatedTime: "30 minutes",
            objectives: [
              "Reuse logic",
              "Simplify components",
              "Prepare for large codebases",
            ],
          },
          {
            day: 5,
            topic: "Dependency Management & Boundaries",
            subTopics: [
              "Avoiding circular dependencies",
              "Module boundaries",
              "Import best practices",
            ],
            explanation:
              "Prevent architectural issues that break builds and cause hidden bugs.",
            content: {
              sections: [
                {
                  title: "Dependency Rules",
                  description: "Dependencies should flow inward, not outward.",
                },
              ],
            },
            tasks: ["Identify circular dependency risk", "Refactor imports"],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Maintain stable architecture",
              "Avoid hidden bugs",
              "Improve build reliability",
            ],
          },
          {
            day: 6,
            topic: "Refactoring Strategies & Tech Debt Control",
            subTopics: [
              "Incremental refactoring",
              "Code smells",
              "When to refactor",
            ],
            explanation:
              "Learn safe refactoring techniques without breaking features.",
            content: {
              sections: [
                {
                  title: "Safe Refactoring",
                  description: "Refactor in small steps with tests.",
                },
              ],
            },
            tasks: ["Refactor a screen safely", "Remove duplicated logic"],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Reduce tech debt",
              "Improve maintainability",
              "Refactor with confidence",
            ],
          },
          {
            day: 7,
            topic: "Week 15 Review & Architecture Mini Project",
            subTopics: ["Folder structure", "Hooks", "Services", "Refactoring"],
            explanation:
              "Refactor an existing app into a scalable architecture.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description:
                    "Take a medium-size app and reorganize it for scale.",
                },
              ],
            },
            tasks: [
              "Restructure folders",
              "Extract hooks and services",
              "Document architecture decisions",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Apply architecture principles",
              "Build scalable codebase",
              "Think like a senior engineer",
            ],
          },
        ],
      },
      {
        week: 16,
        title: "Authentication, Authorization & Secure App Flows",
        weekGoal:
          "Implement secure authentication flows and protect app routes and data",
        weekOutcome:
          "Build login, signup, logout, and protected screens with real-world security practices",
        miniProject:
          "Auth System – Email/Password Login, Protected Routes & Logout Flow",
        canLearn: ["beginners", "professionals"],
        days: [
          {
            day: 1,
            topic: "Authentication Fundamentals",
            subTopics: [
              "What is authentication",
              "Authentication vs authorization",
              "Session vs token-based auth",
              "Common auth flows in mobile apps",
            ],
            explanation:
              "Understand how authentication works conceptually before implementing it in React Native apps.",
            content: {
              introduction:
                "Authentication is the backbone of most real-world apps. Weak auth implementation leads to security risks and broken user experiences.",
              sections: [
                {
                  title: "Authentication vs Authorization",
                  description:
                    "Authentication verifies who you are; authorization defines what you can access.",
                  analogy:
                    "Showing your ID vs being allowed into restricted areas",
                },
                {
                  title: "Common Mobile Auth Flows",
                  description:
                    "Typical login, signup, and session flows used in production apps.",
                },
              ],
            },
            tasks: [
              "Map auth flow of a popular app",
              "Identify authentication vs authorization steps",
            ],
            editorType: "text",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Understand auth concepts",
              "Differentiate auth vs authorization",
              "Prepare for implementation",
            ],
          },
          {
            day: 2,
            topic: "Email & Password Authentication",
            subTopics: [
              "Signup flow",
              "Login flow",
              "Form validation recap",
              "Handling auth errors",
            ],
            explanation:
              "Implement basic email and password authentication safely.",
            content: {
              introduction:
                "Email/password login is still the most common authentication method.",
              sections: [
                {
                  title: "Signup & Login Flow",
                  description: "Create users and authenticate credentials.",
                },
                {
                  title: "Handling Auth Errors",
                  description:
                    "Show clear messages for invalid credentials or network issues.",
                },
              ],
            },
            tasks: ["Build signup screen", "Implement login logic"],
            editorType: "javascript",
            estimatedTime: "30 minutes",
            objectives: [
              "Build basic auth screens",
              "Handle errors gracefully",
              "Improve user trust",
            ],
          },
          {
            day: 3,
            topic: "Persisting Auth State & Auto Login",
            subTopics: [
              "Auth tokens",
              "Persisting login",
              "Auto-login on app launch",
            ],
            explanation: "Keep users logged in across app restarts securely.",
            content: {
              introduction:
                "Users expect apps to remember them. Auth persistence improves UX.",
              sections: [
                {
                  title: "Auth Persistence",
                  description:
                    "Store auth state securely and restore it on launch.",
                },
              ],
            },
            tasks: ["Persist login token", "Restore auth state on app launch"],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Implement auto-login",
              "Improve app experience",
              "Prepare for protected routes",
            ],
          },
          {
            day: 4,
            topic: "Protected Routes & Role-Based Access",
            subTopics: [
              "Protected screens",
              "Auth guards",
              "Role-based access control",
              "Unauthorized handling",
            ],
            explanation:
              "Restrict access to certain screens based on authentication or user roles.",
            content: {
              sections: [
                {
                  title: "Protected Navigation",
                  description: "Prevent unauthorized access to screens.",
                },
                {
                  title: "Role-Based Access",
                  description:
                    "Different permissions for different user roles.",
                },
              ],
            },
            tasks: [
              "Protect a screen behind auth",
              "Add role-based conditional UI",
            ],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Secure app routes",
              "Control access logically",
              "Build real-world auth flows",
            ],
          },
          {
            day: 5,
            topic: "Logout, Token Expiry & Edge Cases",
            subTopics: [
              "Logout flow",
              "Token expiration",
              "Session invalidation",
              "Edge case handling",
            ],
            explanation:
              "Handle logout and expired sessions safely without breaking UX.",
            content: {
              sections: [
                {
                  title: "Logout Flow",
                  description: "Clear auth data and reset app state.",
                },
              ],
            },
            tasks: ["Implement logout", "Handle expired token scenario"],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Handle session lifecycle",
              "Prevent stale sessions",
              "Improve security",
            ],
          },
          {
            day: 6,
            topic: "Security Best Practices in Mobile Apps",
            subTopics: [
              "Avoid storing secrets",
              "Secure storage basics",
              "Common security mistakes",
            ],
            explanation:
              "Apply essential security practices to protect user data.",
            content: {
              sections: [
                {
                  title: "Mobile Security Basics",
                  description: "Minimize attack surface and data exposure.",
                },
              ],
            },
            tasks: [
              "Review auth-related code for risks",
              "Fix insecure patterns",
            ],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Build secure apps",
              "Avoid common vulnerabilities",
              "Think like a security-aware developer",
            ],
          },
          {
            day: 7,
            topic: "Week 16 Review & Auth Mini Project",
            subTopics: ["Login", "Signup", "Protected routes", "Logout"],
            explanation:
              "Build a complete authentication system using best practices.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description:
                    "Build a secure auth flow with protected screens and persistent login.",
                },
              ],
            },
            tasks: [
              "Implement full auth flow",
              "Protect routes",
              "Test edge cases",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Apply auth concepts",
              "Build secure flows",
              "Prepare for real backend integration",
            ],
          },
        ],
      },
    ],
    outcome: "Ship production-ready apps with clean architecture",
  },
  {
    phaseKey: "advanced-systems",
    phase: 5,
    title: "Advanced Production Systems",
    level: "Advanced",
    startWeek: 17,
    endWeek: 21,
    weeks: [
      {
        week: 17,
        title: "Push Notifications, Deep Linking & App Engagement",
        weekGoal:
          "Implement push notifications and deep linking to increase user engagement and retention",
        weekOutcome:
          "Send notifications, handle notification taps, and navigate users to correct app screens",
        miniProject:
          "Engagement Layer – Push Notifications with Deep Link Navigation",
        canLearn: ["professionals"],
        days: [
          {
            day: 1,
            topic: "Understanding Push Notifications",
            subTopics: [
              "What are push notifications",
              "Local vs remote notifications",
              "Use cases in real apps",
              "Notification lifecycle",
            ],
            explanation:
              "Understand how push notifications work and how they re-engage users even when the app is closed.",
            content: {
              introduction:
                "Push notifications are a core engagement tool for modern apps, but misuse can annoy users and cause uninstalls.",
              sections: [
                {
                  title: "Local vs Remote Notifications",
                  description:
                    "Local notifications are triggered by the app, remote notifications are sent from a server.",
                  examples: [
                    "Reminder notifications",
                    "Chat message alerts",
                    "Promotional notifications",
                  ],
                },
                {
                  title: "Notification Lifecycle",
                  description: "From server → device → user tap → app action.",
                },
              ],
              commonMistakes: [
                {
                  mistake: "Sending too many notifications",
                  consequence: "Users disable notifications or uninstall app",
                  fix: "Send relevant, timely notifications only",
                },
              ],
            },
            tasks: [
              "Identify notification use cases in popular apps",
              "Map notification lifecycle on paper",
            ],
            editorType: "text",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Understand notification fundamentals",
              "Think user-first",
              "Prepare for implementation",
            ],
          },
          {
            day: 2,
            topic: "Push Notification Setup (Firebase)",
            subTopics: [
              "Firebase Cloud Messaging (FCM)",
              "Android setup",
              "iOS setup",
              "Permission handling",
            ],
            explanation:
              "Set up push notifications using Firebase Cloud Messaging.",
            content: {
              introduction:
                "FCM is the most common solution for push notifications in React Native apps.",
              sections: [
                {
                  title: "FCM Overview",
                  description:
                    "Send notifications reliably to Android and iOS devices.",
                },
                {
                  title: "Permissions & Tokens",
                  description:
                    "Request user permission and generate device tokens.",
                },
              ],
            },
            tasks: ["Configure Firebase project", "Generate FCM token"],
            editorType: "javascript",
            estimatedTime: "30–35 minutes",
            objectives: [
              "Set up FCM",
              "Handle permissions",
              "Understand device tokens",
            ],
          },
          {
            day: 3,
            topic: "Handling Notifications in App States",
            subTopics: [
              "Foreground notifications",
              "Background notifications",
              "Killed state handling",
              "Notification data payload",
            ],
            explanation:
              "Handle notifications correctly depending on app state.",
            content: {
              sections: [
                {
                  title: "App State Scenarios",
                  description:
                    "Notification behavior differs when app is open, backgrounded, or killed.",
                },
              ],
            },
            tasks: ["Handle foreground notification", "Log payload data"],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Handle all app states",
              "Avoid lost notifications",
              "Prepare for deep linking",
            ],
          },
          {
            day: 4,
            topic: "Deep Linking Basics",
            subTopics: [
              "What is deep linking",
              "URL schemes",
              "Universal links",
              "Common use cases",
            ],
            explanation: "Navigate users to specific screens using links.",
            content: {
              introduction:
                "Deep linking allows users to open a specific screen directly from a link or notification.",
              sections: [
                {
                  title: "Deep Link Types",
                  description: "Custom schemes and universal/app links.",
                },
              ],
            },
            tasks: ["Configure deep linking", "Open app from a URL"],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Understand deep links",
              "Navigate via URLs",
              "Improve user experience",
            ],
          },
          {
            day: 5,
            topic: "Notification → Deep Link Navigation",
            subTopics: [
              "Passing data via notifications",
              "Navigating on notification tap",
              "Handling edge cases",
            ],
            explanation:
              "Connect push notifications with deep links for seamless navigation.",
            content: {
              sections: [
                {
                  title: "End-to-End Flow",
                  description:
                    "Notification tap opens correct screen with context.",
                },
              ],
            },
            tasks: [
              "Navigate to detail screen from notification",
              "Handle missing data gracefully",
            ],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Combine notifications and navigation",
              "Build engagement flows",
              "Handle real-world cases",
            ],
          },
          {
            day: 6,
            topic: "Notification Best Practices & Analytics",
            subTopics: [
              "Notification timing",
              "User opt-in strategies",
              "Tracking opens",
              "Measuring engagement",
            ],
            explanation:
              "Send effective notifications and measure their impact.",
            content: {
              sections: [
                {
                  title: "Best Practices",
                  description: "Respect users and optimize engagement.",
                },
              ],
            },
            tasks: [
              "Design notification strategy",
              "Track notification open event",
            ],
            editorType: "text",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Increase retention",
              "Avoid spammy notifications",
              "Think product-first",
            ],
          },
          {
            day: 7,
            topic: "Week 17 Review & Engagement Mini Project",
            subTopics: [
              "Push notifications",
              "Deep linking",
              "Navigation",
              "Engagement strategy",
            ],
            explanation:
              "Build a complete engagement layer with notifications and deep links.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description:
                    "Send a push notification that deep-links into a specific screen.",
                },
              ],
            },
            tasks: [
              "Implement FCM notifications",
              "Handle deep link navigation",
              "Test all app states",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Apply engagement concepts",
              "Build real-world feature",
              "Prepare for analytics & monitoring",
            ],
          },
        ],
      },
      {
        week: 18,
        title: "Performance Optimization & Smooth UX",
        weekGoal:
          "Optimize React Native apps for smooth performance, fast rendering, and better user experience",
        weekOutcome:
          "Identify performance bottlenecks and apply proven optimization techniques used in production apps",
        miniProject:
          "Performance Sprint – Optimize a Laggy Screen & Large List",
        canLearn: ["professionals"],
        days: [
          {
            day: 1,
            topic: "Understanding Performance in React Native",
            subTopics: [
              "JS thread vs UI thread",
              "Bridge basics",
              "Why apps feel slow",
              "Common performance myths",
            ],
            explanation:
              "Understand how React Native works internally and why performance issues happen.",
            content: {
              introduction:
                "Performance issues are usually architectural or logical mistakes, not React Native limitations.",
              sections: [
                {
                  title: "How React Native Renders",
                  description:
                    "JS thread prepares logic, UI thread handles rendering.",
                  analogy:
                    "Like a waiter (JS) sending orders to the kitchen (UI)",
                },
                {
                  title: "Typical Bottlenecks",
                  description:
                    "Unnecessary re-renders, heavy computations, large lists.",
                },
              ],
            },
            tasks: [
              "Identify slow screen in an app",
              "List possible performance issues",
            ],
            editorType: "text",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Understand RN internals",
              "Identify performance problems",
              "Build performance intuition",
            ],
          },
          {
            day: 2,
            topic: "Avoiding Unnecessary Re-renders",
            subTopics: [
              "Why components re-render",
              "React.memo",
              "useCallback",
              "useMemo",
            ],
            explanation: "Reduce wasted renders to improve responsiveness.",
            content: {
              sections: [
                {
                  title: "Re-render Triggers",
                  description:
                    "State changes, props changes, and parent re-renders.",
                },
                {
                  title: "Memoization Tools",
                  description: "React.memo, useCallback, and useMemo.",
                },
              ],
            },
            tasks: ["Memoize a component", "Fix unnecessary re-renders"],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Reduce renders",
              "Improve responsiveness",
              "Write efficient components",
            ],
          },
          {
            day: 3,
            topic: "Optimizing FlatList & Large Data Sets",
            subTopics: [
              "FlatList vs ScrollView",
              "KeyExtractor",
              "getItemLayout",
              "windowSize & initialNumToRender",
            ],
            explanation: "Handle large lists efficiently without lag.",
            content: {
              sections: [
                {
                  title: "FlatList Optimization",
                  description: "Render only what’s visible.",
                },
              ],
            },
            tasks: ["Optimize a FlatList", "Fix list performance issues"],
            editorType: "javascript",
            estimatedTime: "30 minutes",
            objectives: [
              "Handle large lists",
              "Improve scrolling performance",
              "Avoid memory issues",
            ],
          },
          {
            day: 4,
            topic: "Optimizing Images & Media",
            subTopics: [
              "Image sizing",
              "Caching strategies",
              "FastImage overview",
              "Avoiding memory leaks",
            ],
            explanation:
              "Images are a major source of performance issues in mobile apps.",
            content: {
              sections: [
                {
                  title: "Image Optimization",
                  description:
                    "Reduce size, cache correctly, and avoid unnecessary re-renders.",
                },
              ],
            },
            tasks: ["Optimize image rendering", "Implement caching strategy"],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Optimize media",
              "Reduce memory usage",
              "Improve load time",
            ],
          },
          {
            day: 5,
            topic: "Animations & UI Performance",
            subTopics: [
              "Why animations cause jank",
              "Layout vs transform animations",
              "Reanimated overview",
            ],
            explanation:
              "Create smooth animations without blocking the JS thread.",
            content: {
              sections: [
                {
                  title: "Smooth Animations",
                  description: "Prefer native-driven animations.",
                },
              ],
            },
            tasks: ["Create a smooth animation", "Fix janky animation"],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Build smooth animations",
              "Avoid UI jank",
              "Improve UX polish",
            ],
          },
          {
            day: 6,
            topic: "Profiling & Measuring Performance",
            subTopics: [
              "Why measuring matters",
              "Flipper performance tools",
              "React DevTools profiler",
            ],
            explanation: "Measure performance instead of guessing.",
            content: {
              sections: [
                {
                  title: "Profiling Tools",
                  description: "Use tools to identify real bottlenecks.",
                },
              ],
            },
            tasks: ["Profile a slow screen", "Identify top bottleneck"],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Measure performance",
              "Make data-driven decisions",
              "Optimize confidently",
            ],
          },
          {
            day: 7,
            topic: "Week 18 Review & Performance Mini Project",
            subTopics: ["Rendering", "Lists", "Images", "Animations"],
            explanation: "Optimize a real screen with performance issues.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description: "Take a slow screen and make it smooth.",
                },
              ],
            },
            tasks: [
              "Optimize slow screen",
              "Document before/after improvements",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Apply performance skills",
              "Deliver smooth UX",
              "Think like a performance-focused engineer",
            ],
          },
        ],
      },
      {
        week: 19,
        title: "Offline Support, Caching & Data Synchronization",
        weekGoal:
          "Build apps that work reliably with poor or no internet connectivity",
        weekOutcome:
          "Implement offline-first patterns, caching, and safe data synchronization",
        miniProject: "Offline-First App – Cached Data with Sync on Reconnect",
        canLearn: ["professionals"],
        days: [
          {
            day: 1,
            topic: "Offline-First Mindset",
            subTopics: [
              "Why offline matters",
              "Online vs offline-first apps",
              "Real-world failure scenarios",
              "User expectations",
            ],
            explanation:
              "Understand why modern mobile apps must handle poor connectivity gracefully.",
            content: {
              introduction:
                "Mobile users frequently face unstable networks. Apps that fail offline lose trust quickly.",
              sections: [
                {
                  title: "Offline-First Philosophy",
                  description: "Design assuming the network is unreliable.",
                  analogy:
                    "Like autosave in Google Docs — users don’t think about internet",
                },
              ],
            },
            tasks: [
              "Analyze offline behavior of a popular app",
              "List failure points in a typical API-driven screen",
            ],
            editorType: "text",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Adopt offline-first thinking",
              "Design for real-world conditions",
              "Improve user trust",
            ],
          },
          {
            day: 2,
            topic: "Local Storage & Caching Basics",
            subTopics: [
              "AsyncStorage",
              "MMKV overview",
              "What data should be cached",
              "Cache invalidation basics",
            ],
            explanation:
              "Store and retrieve data locally to support offline usage.",
            content: {
              sections: [
                {
                  title: "Caching Strategy",
                  description: "Cache read-heavy and non-sensitive data.",
                },
              ],
            },
            tasks: [
              "Cache API response locally",
              "Load data from cache on app start",
            ],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Persist data locally",
              "Reduce network dependency",
              "Improve app speed",
            ],
          },
          {
            day: 3,
            topic: "Network Detection & Fallback UI",
            subTopics: [
              "Detecting internet status",
              "Online/offline UI states",
              "Retry patterns",
            ],
            explanation:
              "React to connectivity changes and guide users properly.",
            content: {
              sections: [
                {
                  title: "Network Awareness",
                  description: "React dynamically to connection changes.",
                },
              ],
            },
            tasks: ["Detect offline state", "Show offline fallback UI"],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Handle network changes",
              "Improve UX during failures",
              "Avoid broken screens",
            ],
          },
          {
            day: 4,
            topic: "Syncing Data When Online",
            subTopics: [
              "Queueing offline actions",
              "Sync strategies",
              "Conflict resolution basics",
            ],
            explanation:
              "Safely sync locally stored data when internet is restored.",
            content: {
              sections: [
                {
                  title: "Data Sync Flow",
                  description: "Store → queue → sync → resolve conflicts.",
                },
              ],
            },
            tasks: ["Queue offline action", "Sync data on reconnect"],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Build reliable sync flow",
              "Prevent data loss",
              "Handle edge cases",
            ],
          },
          {
            day: 5,
            topic: "Caching API Responses & Pagination",
            subTopics: [
              "Paginated caching",
              "Stale-while-revalidate",
              "Cache expiration",
            ],
            explanation: "Cache complex API data efficiently.",
            content: {
              sections: [
                {
                  title: "Advanced Caching Patterns",
                  description: "Balance freshness and performance.",
                },
              ],
            },
            tasks: ["Cache paginated list", "Implement cache refresh logic"],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Handle large datasets offline",
              "Optimize API usage",
              "Improve perceived speed",
            ],
          },
          {
            day: 6,
            topic: "Offline Edge Cases & Best Practices",
            subTopics: [
              "Partial data availability",
              "Error recovery",
              "User messaging",
            ],
            explanation:
              "Handle tricky offline scenarios without confusing users.",
            content: {
              sections: [
                {
                  title: "Edge Case Handling",
                  description: "Plan for incomplete or stale data.",
                },
              ],
            },
            tasks: [
              "Handle partial cached data",
              "Improve offline error messages",
            ],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Handle real-world failures",
              "Improve UX clarity",
              "Build robust apps",
            ],
          },
          {
            day: 7,
            topic: "Week 19 Review & Offline Mini Project",
            subTopics: [
              "Caching",
              "Offline UI",
              "Sync logic",
              "Error handling",
            ],
            explanation: "Build an offline-capable feature end-to-end.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description:
                    "Build a screen that works fully offline and syncs on reconnect.",
                },
              ],
            },
            tasks: [
              "Implement offline-first screen",
              "Test with airplane mode",
              "Document sync flow",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Apply offline-first concepts",
              "Build resilient features",
              "Think like a production engineer",
            ],
          },
        ],
      },
      {
        week: 20,
        title: "Analytics, Monitoring & Production Observability",
        weekGoal:
          "Track user behavior, monitor crashes, and gain visibility into app health in production",
        weekOutcome:
          "Integrate analytics and crash reporting to make data-driven product and engineering decisions",
        miniProject:
          "Observability Setup – Analytics Events & Crash Monitoring",
        canLearn: ["professionals"],
        days: [
          {
            day: 1,
            topic: "Why Analytics & Monitoring Matter",
            subTopics: [
              "What is observability",
              "Why logs are not enough",
              "Engineering vs product analytics",
              "Real-world failure stories",
            ],
            explanation:
              "Understand why production visibility is critical for real apps.",
            content: {
              introduction:
                "Once an app is live, you no longer control the environment. Analytics and monitoring become your eyes.",
              sections: [
                {
                  title: "Observability Basics",
                  description:
                    "Knowing what users do and what breaks in production.",
                  analogy: "Like CCTV + health monitor for your app",
                },
              ],
            },
            tasks: [
              "List what you would want to track in a real app",
              "Differentiate product metrics vs technical metrics",
            ],
            editorType: "text",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Understand observability",
              "Think production-first",
              "Prepare for analytics setup",
            ],
          },
          {
            day: 2,
            topic: "User Analytics & Event Tracking",
            subTopics: [
              "Event-based analytics",
              "Screen tracking",
              "Custom events",
              "User properties",
            ],
            explanation: "Track how users actually use your app.",
            content: {
              sections: [
                {
                  title: "Event Design",
                  description: "Good analytics starts with meaningful events.",
                },
              ],
            },
            tasks: ["Track screen views", "Add 3 meaningful custom events"],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Track user behavior",
              "Avoid vanity metrics",
              "Design useful events",
            ],
          },
          {
            day: 3,
            topic: "Crash Reporting & Error Monitoring",
            subTopics: [
              "Crashlytics overview",
              "JS vs native crashes",
              "Error grouping",
              "Reading crash reports",
            ],
            explanation: "Detect and fix crashes users never report.",
            content: {
              sections: [
                {
                  title: "Crash Monitoring",
                  description:
                    "Automatically capture crashes and stack traces.",
                },
              ],
            },
            tasks: [
              "Integrate crash reporting",
              "Trigger and view a test crash",
            ],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Detect crashes",
              "Debug production issues",
              "Reduce user pain",
            ],
          },
          {
            day: 4,
            topic: "Logging, Breadcrumbs & Debug Signals",
            subTopics: [
              "Structured logging",
              "Breadcrumbs",
              "Contextual logs",
              "Avoiding noisy logs",
            ],
            explanation:
              "Add context to crashes and errors for faster debugging.",
            content: {
              sections: [
                {
                  title: "Smart Logging",
                  description:
                    "Logs should explain what happened before failure.",
                },
              ],
            },
            tasks: [
              "Add breadcrumbs around critical flows",
              "Refine noisy logs",
            ],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Improve debuggability",
              "Reduce mean-time-to-fix",
              "Write meaningful logs",
            ],
          },
          {
            day: 5,
            topic: "Performance & Network Monitoring",
            subTopics: [
              "Network request tracking",
              "Slow API detection",
              "App startup time",
              "User-perceived performance",
            ],
            explanation:
              "Monitor performance issues in real user environments.",
            content: {
              sections: [
                {
                  title: "Performance Signals",
                  description:
                    "Measure what users feel, not just what code does.",
                },
              ],
            },
            tasks: ["Track API response time", "Identify slow endpoints"],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Monitor performance",
              "Detect bottlenecks",
              "Improve UX",
            ],
          },
          {
            day: 6,
            topic: "Privacy, Data Ethics & Compliance",
            subTopics: [
              "User consent",
              "What not to track",
              "PII handling",
              "Compliance basics",
            ],
            explanation:
              "Track responsibly without violating user trust or laws.",
            content: {
              sections: [
                {
                  title: "Ethical Analytics",
                  description:
                    "Respect user privacy while collecting insights.",
                },
              ],
            },
            tasks: [
              "Audit analytics events for privacy",
              "Remove unnecessary data",
            ],
            editorType: "text",
            estimatedTime: "20 minutes",
            objectives: [
              "Respect user privacy",
              "Avoid legal issues",
              "Build trust",
            ],
          },
          {
            day: 7,
            topic: "Week 20 Review & Observability Mini Project",
            subTopics: ["Analytics", "Crashes", "Logs", "Performance"],
            explanation: "Add full observability to an existing app.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description:
                    "Instrument an app with analytics and crash monitoring.",
                },
              ],
            },
            tasks: [
              "Track key user flows",
              "Monitor crashes",
              "Review analytics dashboard",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Apply observability concepts",
              "Think data-driven",
              "Operate apps in production",
            ],
          },
        ],
      },
      {
        week: 21,
        title: "Security, Permissions & App Hardening",
        weekGoal:
          "Protect user data, secure app behavior, and avoid common mobile security vulnerabilities",
        weekOutcome:
          "Apply practical security best practices used in production-grade React Native apps",
        miniProject:
          "Security Audit – Harden an Existing App Against Common Threats",
        canLearn: ["professionals"],
        days: [
          {
            day: 1,
            topic: "Mobile App Security Fundamentals",
            subTopics: [
              "Why mobile security matters",
              "Client-side risks",
              "Threat models",
              "Common attack vectors",
            ],
            explanation:
              "Understand how mobile apps are attacked and where React Native apps are vulnerable.",
            content: {
              introduction:
                "Mobile apps run on user-controlled devices, making security a shared responsibility between client and server.",
              sections: [
                {
                  title: "Common Mobile Threats",
                  description:
                    "Reverse engineering, man-in-the-middle attacks, and data leakage.",
                  analogy:
                    "Like locking your house even when you trust the neighborhood",
                },
              ],
            },
            tasks: [
              "List sensitive data in a sample app",
              "Identify possible attack points",
            ],
            editorType: "text",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Think security-first",
              "Understand threat models",
              "Avoid naive assumptions",
            ],
          },
          {
            day: 2,
            topic: "Secure Storage & Sensitive Data Handling",
            subTopics: [
              "Secure storage vs AsyncStorage",
              "What should never be stored",
              "Token storage strategies",
            ],
            explanation: "Store sensitive information safely on user devices.",
            content: {
              sections: [
                {
                  title: "Secure Storage Basics",
                  description: "Use encrypted storage for secrets and tokens.",
                },
              ],
            },
            tasks: [
              "Move token from AsyncStorage to secure storage",
              "Audit stored data",
            ],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Protect sensitive data",
              "Reduce breach risk",
              "Follow best practices",
            ],
          },
          {
            day: 3,
            topic: "Permissions & Privacy-Sensitive APIs",
            subTopics: [
              "Runtime permissions",
              "Camera, location, contacts",
              "Permission denial handling",
            ],
            explanation:
              "Request permissions responsibly and only when needed.",
            content: {
              sections: [
                {
                  title: "Permission Strategy",
                  description:
                    "Ask late, explain clearly, handle denial gracefully.",
                },
              ],
            },
            tasks: [
              "Implement permission request flow",
              "Handle denied permission UI",
            ],
            editorType: "javascript",
            estimatedTime: "25 minutes",
            objectives: [
              "Respect user privacy",
              "Avoid permission abuse",
              "Improve trust",
            ],
          },
          {
            day: 4,
            topic: "Network Security & API Protection",
            subTopics: [
              "HTTPS enforcement",
              "Certificate pinning basics",
              "API key exposure risks",
            ],
            explanation: "Secure data in transit between app and backend.",
            content: {
              sections: [
                {
                  title: "Secure Network Communication",
                  description: "Prevent interception and tampering.",
                },
              ],
            },
            tasks: ["Verify HTTPS usage", "Identify exposed API keys"],
            editorType: "javascript",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Secure network traffic",
              "Prevent MITM attacks",
              "Understand API security limits",
            ],
          },
          {
            day: 5,
            topic: "Protecting Business Logic & Code",
            subTopics: [
              "Client-side trust issues",
              "Code obfuscation basics",
              "What logic should stay on backend",
            ],
            explanation: "Prevent abuse of client-side logic and APIs.",
            content: {
              sections: [
                {
                  title: "Client vs Server Responsibility",
                  description: "Never trust the client fully.",
                },
              ],
            },
            tasks: [
              "Identify logic that should be server-side",
              "Remove sensitive client checks",
            ],
            editorType: "javascript",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Reduce abuse risk",
              "Design secure flows",
              "Think backend-aware",
            ],
          },
          {
            day: 6,
            topic: "Handling Security Incidents & Updates",
            subTopics: [
              "Security patches",
              "Hotfix releases",
              "User communication",
            ],
            explanation:
              "Respond quickly and safely to discovered vulnerabilities.",
            content: {
              sections: [
                {
                  title: "Incident Response",
                  description:
                    "Fix fast, communicate clearly, prevent recurrence.",
                },
              ],
            },
            tasks: ["Simulate a security fix", "Prepare update notes"],
            editorType: "text",
            estimatedTime: "20 minutes",
            objectives: [
              "Handle incidents professionally",
              "Reduce user impact",
              "Maintain trust",
            ],
          },
          {
            day: 7,
            topic: "Week 21 Review & Security Mini Project",
            subTopics: [
              "Secure storage",
              "Permissions",
              "Network security",
              "Code protection",
            ],
            explanation: "Perform a basic security audit and harden an app.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description: "Audit and improve security of an existing app.",
                },
              ],
            },
            tasks: [
              "Audit security risks",
              "Apply fixes",
              "Document security decisions",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Apply security practices",
              "Think defensively",
              "Build safer apps",
            ],
          },
        ],
      },
    ],
    outcome: "Think and work like a professional React Native engineer",
  },
  {
    phaseKey: "career-capstone",
    phase: 6,
    title: "Career, CI/CD & Capstone",
    level: "Job Ready",
    startWeek: 22,
    endWeek: 24,
    weeks: [
      {
        week: 22,
        title: "CI/CD, Automation & Team Workflows",
        weekGoal:
          "Automate builds, testing, and releases to ship faster and safer",
        weekOutcome:
          "Set up CI/CD pipelines and collaborate using professional team workflows",
        miniProject: "CI/CD Pipeline – Automated Build, Test & Release Flow",
        canLearn: ["professionals"],
        days: [
          {
            day: 1,
            topic: "What is CI/CD & Why It Matters",
            subTopics: [
              "Continuous Integration",
              "Continuous Delivery vs Deployment",
              "Manual vs automated workflows",
              "Real-world team problems",
            ],
            explanation:
              "Understand how teams ship code reliably using automation.",
            content: {
              introduction:
                "CI/CD removes human error, speeds up delivery, and increases confidence in releases.",
              sections: [
                {
                  title: "CI/CD Basics",
                  description:
                    "Automated testing and builds on every code change.",
                  analogy:
                    "Like an assembly line with quality checks at every step",
                },
              ],
            },
            tasks: [
              "Draw a basic CI/CD flow",
              "Identify manual steps in your current workflow",
            ],
            editorType: "text",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Understand CI/CD",
              "Think automation-first",
              "Prepare for pipeline setup",
            ],
          },
          {
            day: 2,
            topic: "Version Control Workflows (Git)",
            subTopics: [
              "Branching strategies",
              "Feature branches",
              "Pull requests",
              "Code reviews",
            ],
            explanation: "Work safely in teams using Git workflows.",
            content: {
              sections: [
                {
                  title: "Team Git Workflow",
                  description: "Feature → review → merge → release.",
                },
              ],
            },
            tasks: ["Create feature branch", "Open a pull request"],
            editorType: "bash",
            estimatedTime: "25 minutes",
            objectives: [
              "Collaborate safely",
              "Avoid merge conflicts",
              "Follow industry practices",
            ],
          },
          {
            day: 3,
            topic: "Automated Testing in CI",
            subTopics: [
              "Running tests in CI",
              "Failing builds",
              "Test coverage basics",
            ],
            explanation: "Ensure broken code never reaches production.",
            content: {
              sections: [
                {
                  title: "Tests in CI",
                  description: "Run Jest and lint checks automatically.",
                },
              ],
            },
            tasks: ["Configure test step in CI", "Fail build on test error"],
            editorType: "yaml",
            estimatedTime: "25–30 minutes",
            objectives: [
              "Automate testing",
              "Prevent regressions",
              "Increase confidence",
            ],
          },
          {
            day: 4,
            topic: "Automated Builds for Mobile Apps",
            subTopics: [
              "Android CI builds",
              "iOS CI builds",
              "Handling secrets in CI",
            ],
            explanation: "Build mobile apps automatically in CI pipelines.",
            content: {
              sections: [
                {
                  title: "Mobile Build Automation",
                  description:
                    "Generate APK/AAB and iOS archives automatically.",
                },
              ],
            },
            tasks: ["Set up Android build step", "Configure CI secrets"],
            editorType: "yaml",
            estimatedTime: "30 minutes",
            objectives: [
              "Automate builds",
              "Reduce manual errors",
              "Speed up releases",
            ],
          },
          {
            day: 5,
            topic: "Release Automation & Distribution",
            subTopics: [
              "Internal testing builds",
              "Beta distribution",
              "Release channels",
            ],
            explanation:
              "Distribute builds to testers and teams automatically.",
            content: {
              sections: [
                {
                  title: "Automated Distribution",
                  description: "Deliver builds without manual upload.",
                },
              ],
            },
            tasks: ["Distribute beta build", "Tag a release version"],
            editorType: "yaml",
            estimatedTime: "25 minutes",
            objectives: [
              "Automate releases",
              "Improve testing feedback",
              "Ship faster",
            ],
          },
          {
            day: 6,
            topic: "Team Collaboration & Code Reviews",
            subTopics: [
              "Review checklist",
              "Giving constructive feedback",
              "Reviewing PRs efficiently",
            ],
            explanation: "Improve code quality through collaboration.",
            content: {
              sections: [
                {
                  title: "Effective Code Reviews",
                  description: "Focus on logic, readability, and risk.",
                },
              ],
            },
            tasks: ["Review a pull request", "Write actionable feedback"],
            editorType: "text",
            estimatedTime: "20–25 minutes",
            objectives: [
              "Improve team quality",
              "Learn from peers",
              "Build collaboration skills",
            ],
          },
          {
            day: 7,
            topic: "Week 22 Review & CI/CD Mini Project",
            subTopics: [
              "CI pipeline",
              "Automated tests",
              "Automated builds",
              "Release flow",
            ],
            explanation:
              "Build a complete CI/CD pipeline for a React Native app.",
            content: {
              sections: [
                {
                  title: "Mini Project Overview",
                  description: "Automate build, test, and release pipeline.",
                },
              ],
            },
            tasks: [
              "Set up full CI/CD pipeline",
              "Run tests and builds automatically",
              "Document pipeline steps",
            ],
            editorType: null,
            estimatedTime: "60–90 minutes",
            objectives: [
              "Apply CI/CD concepts",
              "Automate workflow",
              "Ship like a professional team",
            ],
          },
        ],
      },
      {
        week: 24,
        title: "Capstone Project, Real-World Simulation & Graduation",
        weekGoal:
          "Apply everything learned to build, polish, and present a production-grade React Native application",
        weekOutcome:
          "Graduate with a real-world app, strong confidence, and industry-ready mindset",
        miniProject:
          "Capstone App – End-to-End Production-Ready React Native Application",
        canLearn: ["professionals"],
        days: [
          {
            day: 1,
            topic: "Capstone Project Planning & Scope Finalization",
            subTopics: [
              "Choosing the right project idea",
              "Defining MVP vs nice-to-have features",
              "User personas & use cases",
              "Tech stack finalization",
            ],
            explanation:
              "Plan a realistic, production-focused capstone project instead of an over-scoped demo app.",
            content: {
              introduction:
                "Great engineers don’t start by coding — they start by defining scope and constraints clearly.",
              sections: [
                {
                  title: "Capstone Selection Criteria",
                  description:
                    "Choose a project that shows real-world complexity.",
                  examples: [
                    "Auth-based app (role-based)",
                    "API-driven dashboard",
                    "Offline-capable feature",
                    "Push notifications",
                  ],
                },
                {
                  title: "MVP Definition",
                  description: "Cut scope ruthlessly to finish strong.",
                  analogy:
                    "A finished small product beats an unfinished big idea",
                },
              ],
            },
            tasks: [
              "Finalize capstone idea",
              "Write MVP feature list",
              "Define success criteria",
            ],
            editorType: "text",
            estimatedTime: "30–40 minutes",
            objectives: [
              "Plan realistically",
              "Avoid scope creep",
              "Think like a product engineer",
            ],
          },
          {
            day: 2,
            topic: "Architecture Setup & Project Skeleton",
            subTopics: [
              "Folder structure setup",
              "Navigation flow",
              "State management choice",
              "Environment config",
            ],
            explanation:
              "Lay a strong architectural foundation before feature development.",
            content: {
              sections: [
                {
                  title: "Scalable Skeleton",
                  description:
                    "Structure the project like a real production app.",
                },
              ],
            },
            tasks: [
              "Set up project skeleton",
              "Configure navigation & state",
              "Add env configs",
            ],
            editorType: "javascript",
            estimatedTime: "40–50 minutes",
            objectives: [
              "Apply architecture skills",
              "Avoid early tech debt",
              "Prepare for scale",
            ],
          },
          {
            day: 3,
            topic: "Core Feature Implementation",
            subTopics: [
              "Auth flow",
              "API integration",
              "Core user journey",
              "Error handling",
            ],
            explanation: "Build the most important user-facing features first.",
            content: {
              sections: [
                {
                  title: "Feature-First Development",
                  description: "Focus on user value, not edge features.",
                },
              ],
            },
            tasks: [
              "Implement main user flow",
              "Handle loading & error states",
            ],
            editorType: "javascript",
            estimatedTime: "60–90 minutes",
            objectives: [
              "Deliver core value",
              "Build confidence",
              "Apply multiple skills together",
            ],
          },
          {
            day: 4,
            topic: "Advanced Features, Performance & Stability",
            subTopics: [
              "Offline support",
              "Performance optimization",
              "Security checks",
              "Crash prevention",
            ],
            explanation: "Polish the app like a real production release.",
            content: {
              sections: [
                {
                  title: "Production Polish",
                  description:
                    "Stability and performance separate hobby apps from professional ones.",
                },
              ],
            },
            tasks: [
              "Optimize slow screens",
              "Add offline fallback",
              "Fix critical warnings",
            ],
            editorType: "javascript",
            estimatedTime: "45–60 minutes",
            objectives: [
              "Improve reliability",
              "Deliver smooth UX",
              "Think production-first",
            ],
          },
          {
            day: 5,
            topic: "Testing, Analytics & Final QA",
            subTopics: [
              "Critical path testing",
              "Crash monitoring",
              "Analytics events",
              "Manual QA checklist",
            ],
            explanation:
              "Ensure the app is stable and observable before presentation.",
            content: {
              sections: [
                {
                  title: "Final QA Mindset",
                  description:
                    "Assume users will break things — prepare for it.",
                },
              ],
            },
            tasks: [
              "Add basic tests",
              "Verify analytics events",
              "Perform full QA pass",
            ],
            editorType: "javascript",
            estimatedTime: "40–50 minutes",
            objectives: [
              "Ship with confidence",
              "Catch last-minute bugs",
              "Prepare for demo",
            ],
          },
          {
            day: 6,
            topic: "Build, Demo Prep & Documentation",
            subTopics: [
              "Production build generation",
              "README writing",
              "Architecture documentation",
              "Demo storytelling",
            ],
            explanation: "Prepare to present your project professionally.",
            content: {
              sections: [
                {
                  title: "Professional Presentation",
                  description:
                    "How you present matters as much as what you built.",
                },
              ],
            },
            tasks: [
              "Generate production build",
              "Write README",
              "Prepare demo walkthrough",
            ],
            editorType: "text",
            estimatedTime: "40–50 minutes",
            objectives: [
              "Present professionally",
              "Communicate clearly",
              "Stand out in interviews",
            ],
          },
          {
            day: 7,
            topic: "Graduation, Reflection & Next Steps",
            subTopics: [
              "Project retrospective",
              "Skill assessment",
              "Career roadmap",
              "Next learning goals",
            ],
            explanation:
              "Reflect on growth and plan the next phase of your career.",
            content: {
              sections: [
                {
                  title: "Engineer Mindset Reflection",
                  description:
                    "Growth comes from reflection, not just completion.",
                },
              ],
            },
            tasks: [
              "Write project retrospective",
              "List strengths & gaps",
              "Define next 90-day plan",
            ],
            editorType: "text",
            estimatedTime: "30–40 minutes",
            objectives: [
              "Recognize growth",
              "Build long-term plan",
              "Graduate with clarity and confidence",
            ],
          },
        ],
      },
    ],
    outcome: "Portfolio-ready, interview-ready, production confident",
  },
];

export const REACT_NATIVE_DEVELOPER_PHASES = [
  {
    key: "foundation",
    phase: 1,
    title: "Foundations & React Native Basics",
    level: "Beginner",
    duration: "4 weeks",
  },
  {
    key: "core-app",
    phase: 2,
    title: "Core App Development",
    level: "Beginner to Intermediate",
    duration: "4 weeks",
  },
  {
    key: "quality-performance",
    phase: 3,
    title: "Quality, Performance & Stability",
    level: "Intermediate",
    duration: "5 weeks",
  },
  {
    key: "production-release",
    phase: 4,
    title: "Production, Architecture & Release",
    level: "Intermediate to Advanced",
    duration: "2 weeks",
  },
  {
    key: "advanced-systems",
    phase: 5,
    title: "Advanced Production Systems",
    level: "Advanced",
    duration: "5 weeks",
  },
  {
    key: "career-capstone",
    phase: 6,
    title: "Career, CI/CD & Capstone",
    level: "Job Ready",
    duration: "2 weeks",
  },
];

const react_native_roadmap = {
  phases: [
    {
      phaseKey: "foundation-js",
      phase: 1,
      title: "JavaScript Foundations",
      startWeek: 1,
      endWeek: 2,
      weeks: [
        {
          week: 1,
          title: "JavaScript Fundamentals",
          days: [
            {
              day: 1,
              topic: "JavaScript Execution & Variables",
              topics: [
                "JavaScript execution model",
                "Call stack",
                "var vs let vs const",
                "Block scope",
              ],
            },
            {
              day: 2,
              topic: "Functions & Closures",
              topics: [
                "Function declarations",
                "Arrow functions",
                "Closures",
                "Closures in React hooks",
              ],
            },
            {
              day: 3,
              topic: "Arrays & Objects",
              topics: [
                "Array methods",
                "map, filter, reduce",
                "Object spread",
                "Immutability",
              ],
            },
            {
              day: 4,
              topic: "Async JavaScript",
              topics: ["Promises", "async/await", "try/catch", "Async bugs"],
            },
            {
              day: 5,
              topic: "Modern ES6 Patterns",
              topics: [
                "Destructuring",
                "Spread operator",
                "Default parameters",
              ],
            },
            {
              day: 6,
              topic: "JavaScript for Mobile Performance",
              topics: [
                "Memory considerations",
                "Avoid heavy loops",
                "Performance mindset",
              ],
            },
            {
              day: 7,
              topic: "Week 1 Review & Mini Project",
              topics: [
                "JS fundamentals recap",
                "Async handling",
                "Utility functions",
              ],
            },
          ],
        },
        {
          week: 2,
          title: "Advanced JavaScript & TypeScript Basics",
          days: [
            {
              day: 1,
              topic: "Error Handling Deep Dive",
              topics: ["try/catch", "Custom errors", "Graceful failure"],
            },
            {
              day: 2,
              topic: "Advanced Array & Object Patterns",
              topics: ["Nested data handling", "Deep cloning", "Safe updates"],
            },
            {
              day: 3,
              topic: "TypeScript Introduction",
              topics: ["Why TypeScript", "Basic types", "Type inference"],
            },
            {
              day: 4,
              topic: "TypeScript Objects & Functions",
              topics: [
                "Typing objects",
                "Typing functions",
                "Optional properties",
              ],
            },
            {
              day: 5,
              topic: "TypeScript with Async Code",
              topics: ["Typing async functions", "API response types"],
            },
            {
              day: 6,
              topic: "JS + TS Review",
              topics: ["JS vs TS", "When to use what"],
            },
            {
              day: 7,
              topic: "Week 2 Mini Project",
              topics: ["Typed utility module", "Async helpers"],
            },
          ],
        },
      ],
    },

    {
      phaseKey: "react-fundamentals",
      phase: 2,
      title: "React Fundamentals",
      startWeek: 3,
      endWeek: 4,
      weeks: [
        {
          week: 3,
          title: "React Core Concepts",
          days: [
            {
              day: 1,
              topic: "What is React & JSX",
              topics: ["React basics", "Component-based architecture", "JSX"],
            },
            {
              day: 2,
              topic: "Functional Components & Props",
              topics: ["Functional components", "Props", "Reusable components"],
            },
            {
              day: 3,
              topic: "State & useState Hook",
              topics: ["State concept", "useState", "Re-rendering"],
            },
            {
              day: 4,
              topic: "Handling Events",
              topics: ["Event handlers", "Passing functions as props"],
            },
            {
              day: 5,
              topic: "Conditional Rendering",
              topics: ["Ternary rendering", "Logical AND rendering"],
            },
            {
              day: 6,
              topic: "Component Composition",
              topics: ["Splitting components", "Best practices"],
            },
            {
              day: 7,
              topic: "Week 3 Mini Project",
              topics: ["Counter screen", "Profile card", "Conditional UI"],
            },
          ],
        },
        {
          week: 4,
          title: "React Hooks & Optimization",
          days: [
            {
              day: 1,
              topic: "useEffect Hook",
              topics: ["Side effects", "Dependency array"],
            },
            {
              day: 2,
              topic: "Data Fetching in React",
              topics: ["API calls", "Loading & error states"],
            },
            {
              day: 3,
              topic: "useMemo & useCallback",
              topics: ["Performance optimization", "Avoid re-renders"],
            },
            {
              day: 4,
              topic: "Custom Hooks",
              topics: ["Why custom hooks", "Creating hooks"],
            },
            {
              day: 5,
              topic: "React with TypeScript",
              topics: ["Typing props", "Typing state"],
            },
            {
              day: 6,
              topic: "React Best Practices",
              topics: ["Folder structure", "Clean code"],
            },
            {
              day: 7,
              topic: "Week 4 Mini Project",
              topics: ["React dashboard", "Hooks-based UI"],
            },
          ],
        },
      ],
    },

    {
      phaseKey: "react-native-core",
      phase: 3,
      title: "React Native Core",
      startWeek: 5,
      endWeek: 6,
      weeks: [
        {
          week: 5,
          title: "Expo Setup & Core Components",
          days: [
            {
              day: 1,
              topic: "Expo Setup",
              topics: ["Expo CLI", "Project structure"],
            },
            {
              day: 2,
              topic: "Core Components",
              topics: ["View", "Text", "Image"],
            },
            {
              day: 3,
              topic: "Inputs & Forms",
              topics: ["TextInput", "KeyboardAvoidingView"],
            },
            {
              day: 4,
              topic: "Scroll Containers",
              topics: ["ScrollView", "SafeAreaView"],
            },
            { day: 5, topic: "Lists", topics: ["FlatList", "SectionList"] },
            {
              day: 6,
              topic: "Core UI Patterns",
              topics: ["Cards", "Buttons", "Loaders"],
            },
            {
              day: 7,
              topic: "Week 5 Mini Project",
              topics: ["Basic RN screens"],
            },
          ],
        },
        {
          week: 6,
          title: "Styling & Responsive Layout",
          days: [
            { day: 1, topic: "StyleSheet Basics", topics: ["StyleSheet API"] },
            {
              day: 2,
              topic: "Flexbox Layout",
              topics: ["flexDirection", "justifyContent", "alignItems"],
            },
            {
              day: 3,
              topic: "Responsive Design",
              topics: ["Dimensions", "Scaling"],
            },
            {
              day: 4,
              topic: "Tablet Layouts",
              topics: ["Grid layout", "Adaptive UI"],
            },
            {
              day: 5,
              topic: "Light & Dark Theme",
              topics: ["Theme context", "Color schemes"],
            },
            {
              day: 6,
              topic: "UI Best Practices",
              topics: ["Consistency", "Accessibility"],
            },
            {
              day: 7,
              topic: "Week 6 Mini Project",
              topics: ["Responsive themed app"],
            },
          ],
        },
      ],
    },

    {
      phaseKey: "navigation-data",
      phase: 4,
      title: "Navigation, APIs & Storage",
      startWeek: 7,
      endWeek: 9,
      weeks: [
        {
          week: 7,
          title: "Navigation System",
          days: [
            {
              day: 1,
              topic: "Navigation Basics",
              topics: ["React Navigation"],
            },
            {
              day: 2,
              topic: "Stack Navigation",
              topics: ["Screens", "Params"],
            },
            { day: 3, topic: "Tab Navigation", topics: ["Bottom tabs"] },
            { day: 4, topic: "Drawer Navigation", topics: ["Side menu"] },
            { day: 5, topic: "Navigation Patterns", topics: ["Auth flow"] },
            { day: 6, topic: "Deep Linking", topics: ["URL handling"] },
            {
              day: 7,
              topic: "Week 7 Mini Project",
              topics: ["Multi-screen app"],
            },
          ],
        },
        {
          week: 8,
          title: "API Integration",
          days: [
            { day: 1, topic: "REST APIs", topics: ["HTTP methods"] },
            { day: 2, topic: "Axios & Fetch", topics: ["API calls"] },
            {
              day: 3,
              topic: "Loading & Error States",
              topics: ["UX handling"],
            },
            { day: 4, topic: "Authentication APIs", topics: ["Login flow"] },
            { day: 5, topic: "Token Handling", topics: ["JWT"] },
            {
              day: 6,
              topic: "API Best Practices",
              topics: ["Retry", "Timeout"],
            },
            {
              day: 7,
              topic: "Week 8 Mini Project",
              topics: ["Auth based app"],
            },
          ],
        },
        {
          week: 9,
          title: "Local Storage",
          days: [
            { day: 1, topic: "AsyncStorage", topics: ["Key-value storage"] },
            { day: 2, topic: "Secure Storage", topics: ["Sensitive data"] },
            { day: 3, topic: "Persisting State", topics: ["Session handling"] },
            { day: 4, topic: "Logout Flow", topics: ["Clearing storage"] },
            { day: 5, topic: "Offline Basics", topics: ["Cached data"] },
            { day: 6, topic: "Storage Patterns", topics: ["Best practices"] },
            {
              day: 7,
              topic: "Week 9 Mini Project",
              topics: ["Persistent app"],
            },
          ],
        },
      ],
    },

    {
      phaseKey: "advanced-native",
      phase: 5,
      title: "Animations, Media & Permissions",
      startWeek: 10,
      endWeek: 12,
      weeks: [
        {
          week: 10,
          title: "Animations",
          days: [
            { day: 1, topic: "Animated API", topics: ["Basic animations"] },
            { day: 2, topic: "Layout Animations", topics: ["Layout changes"] },
            { day: 3, topic: "Reanimated Basics", topics: ["Shared values"] },
            { day: 4, topic: "Scroll Animations", topics: ["Parallax"] },
            { day: 5, topic: "Animation Performance", topics: ["60 FPS"] },
            { day: 6, topic: "Animation Patterns", topics: ["UX patterns"] },
            {
              day: 7,
              topic: "Week 10 Mini Project",
              topics: ["Animated list"],
            },
          ],
        },
        {
          week: 11,
          title: "Media Handling",
          days: [
            { day: 1, topic: "Image Picker", topics: ["Gallery", "Camera"] },
            { day: 2, topic: "Video Picker", topics: ["Video handling"] },
            { day: 3, topic: "Media Upload", topics: ["Multipart upload"] },
            {
              day: 4,
              topic: "Permissions Basics",
              topics: ["Android permissions"],
            },
            { day: 5, topic: "iOS Permissions", topics: ["Info.plist"] },
            { day: 6, topic: "Permission UX", topics: ["Graceful denial"] },
            {
              day: 7,
              topic: "Week 11 Mini Project",
              topics: ["Media upload app"],
            },
          ],
        },
        {
          week: 12,
          title: "System & Polish",
          days: [
            { day: 1, topic: "StatusBar", topics: ["System UI"] },
            {
              day: 2,
              topic: "Keyboard Handling",
              topics: ["KeyboardAvoidingView"],
            },
            { day: 3, topic: "Accessibility", topics: ["Screen readers"] },
            { day: 4, topic: "Performance Review", topics: ["Optimizations"] },
            { day: 5, topic: "Error Boundaries", topics: ["Crash prevention"] },
            { day: 6, topic: "App Polish", topics: ["Final touches"] },
            { day: 7, topic: "Week 12 Mini Project", topics: ["Polished app"] },
          ],
        },
      ],
    },
    {
      phaseKey: "architecture-firebase",
      phase: 6, // (number aap apne flow ke hisaab se set kar sakte ho)
      title: "Project Structure & Firebase Fundamentals",
      level: "Intermediate → Advanced",
      startWeek: 13,
      endWeek: 13,
      weeks: [
        {
          week: 13,
          title: "Folder Structure & Firebase Basics",
          days: [
            {
              day: 1,
              topic: "React Native Folder Structure",
              topics: [
                "Feature-based structure",
                "screens, components, hooks",
                "services, utils, constants",
              ],
            },
            {
              day: 2,
              topic: "Clean Architecture Principles",
              topics: [
                "Separation of concerns",
                "UI vs business logic",
                "Reusable components",
              ],
            },
            {
              day: 3,
              topic: "Firebase Introduction & Setup",
              topics: [
                "Firebase overview",
                "Project creation",
                "SDK integration",
              ],
            },
            {
              day: 4,
              topic: "Firebase Authentication",
              topics: [
                "Email/password auth",
                "Login & signup flow",
                "Auth state persistence",
              ],
            },
            {
              day: 5,
              topic: "Firestore Database Basics",
              topics: [
                "Collections & documents",
                "CRUD operations",
                "Basic queries",
              ],
            },
            {
              day: 6,
              topic: "Firebase Integration Patterns",
              topics: [
                "Service layer",
                "Error handling",
                "Security rules overview",
              ],
            },
            {
              day: 7,
              topic: "Mini Project",
              topics: [
                "Firebase auth app",
                "Firestore CRUD",
                "Clean folder structure",
              ],
            },
          ],
        },
      ],
    },
    {
      phaseKey: "state-publish",
      phase: 6,
      title: "State Management & Publishing",
      startWeek: 13,
      endWeek: 14,
      weeks: [
        {
          week: 14,
          title: "Redux State Management",
          days: [
            { day: 1, topic: "Redux Basics", topics: ["Store", "Reducers"] },
            { day: 2, topic: "Redux Toolkit", topics: ["Slices"] },
            { day: 3, topic: "Async Thunks", topics: ["API state"] },
            {
              day: 4,
              topic: "Global State Patterns",
              topics: ["Best practices"],
            },
            { day: 5, topic: "Redux with RN", topics: ["Integration"] },
            { day: 6, topic: "Refactoring to Redux", topics: ["Migration"] },
            { day: 7, topic: "Week 13 Mini Project", topics: ["Redux app"] },
          ],
        },
        {
          week: 15,
          title: "App Publishing",
          days: [
            { day: 1, topic: "Android Build", topics: ["APK", "AAB"] },
            { day: 2, topic: "Play Store Process", topics: ["Listing"] },
            { day: 3, topic: "iOS Build Basics", topics: ["Certificates"] },
            {
              day: 4,
              topic: "App Store Submission",
              topics: ["Review process"],
            },
            { day: 5, topic: "Release Management", topics: ["Versioning"] },
            { day: 6, topic: "Post-Release", topics: ["Monitoring"] },
            {
              day: 7,
              topic: "Week 14 Mini Project",
              topics: ["Production build"],
            },
          ],
        },
      ],
    },

    {
      phaseKey: "projects",
      phase: 7,
      title: "Major Projects",
      startWeek: 15,
      endWeek: 17,
      weeks: [
        {
          week: 16,
          title: "Marketplace App",
          days: [
            { day: 1, topic: "Project Setup", topics: ["Architecture"] },
            { day: 2, topic: "Listings", topics: ["CRUD"] },
            { day: 3, topic: "Search & Filters", topics: ["UX"] },
            { day: 4, topic: "Chat Integration", topics: ["Messaging"] },
            { day: 5, topic: "Media Upload", topics: ["Images"] },
            { day: 6, topic: "Polish & Optimize", topics: ["Performance"] },
            { day: 7, topic: "Project Review", topics: ["Code review"] },
          ],
        },
        {
          week: 17,
          title: "Chat Application",
          days: [
            { day: 1, topic: "Chat UI", topics: ["Message bubbles"] },
            { day: 2, topic: "Realtime Logic", topics: ["Updates"] },
            { day: 3, topic: "Media Messages", topics: ["Images"] },
            { day: 4, topic: "State Handling", topics: ["Redux"] },
            { day: 5, topic: "Edge Cases", topics: ["Offline"] },
            { day: 6, topic: "Testing", topics: ["Manual testing"] },
            { day: 7, topic: "Project Review", topics: ["Improvements"] },
          ],
        },
        {
          week: 18,
          title: "Capstone App",
          days: [
            { day: 1, topic: "Idea & Planning", topics: ["Feature list"] },
            { day: 2, topic: "Core Features", topics: ["Implementation"] },
            { day: 3, topic: "Advanced Features", topics: ["Enhancements"] },
            { day: 4, topic: "Optimization", topics: ["Performance"] },
            { day: 5, topic: "Final Polish", topics: ["UX"] },
            { day: 6, topic: "Deployment Prep", topics: ["Build"] },
            { day: 7, topic: "Final Demo", topics: ["Presentation"] },
          ],
        },
      ],
    },

    {
      phaseKey: "interview",
      phase: 8,
      title: "Interview & Job Preparation",
      startWeek: 18,
      endWeek: 18,
      weeks: [
        {
          week: 18,
          title: "Interview Preparation",
          days: [
            { day: 1, topic: "JavaScript Interview", topics: ["40 questions"] },
            { day: 2, topic: "React Interview", topics: ["40 questions"] },
            {
              day: 3,
              topic: "React Native Interview",
              topics: ["40 questions"],
            },
            {
              day: 4,
              topic: "Resume Building",
              topics: ["Projects", "Skills"],
            },
            { day: 5, topic: "GitHub Portfolio", topics: ["Repositories"] },
            { day: 6, topic: "Mock Interviews", topics: ["Practice"] },
            { day: 7, topic: "Job Applications", topics: ["Strategy"] },
          ],
        },
      ],
    },
  ],
};
